import {
    Calendar,
    Decoration,
    Event,
    Group,
    GroupMember,
    GroupMemberStatus,
    OwnitBackupEnvelope,
    OwnitDataSnapshot,
    OwnitPersistedState,
    ReminderRule,
    ReminderSetting,
    StickerAsset,
    SyncRecordSource,
    Theme,
    Todo,
    TodoCategory,
    TodoCompletionRecord,
    TodoDailyCategoryStat,
    TodoDailyRecord,
    TodoDiaryEntry,
    TodoGoal,
    TodoPostponeRecord,
    TodoReaction,
    TodoRoutine,
    TodoTimerSession,
    User
} from './models';
import { canManageEvent, canManageGroup, canViewCalendar, canViewEvent, canViewGroup, hasPermission } from './permissions';
import { DEFAULT_GUEST_USER_ID, OWNIT_DATA_VERSION, OWNIT_DEFAULT_THEME_ID, createDefaultCalendars, createDefaultThemes, createSampleData, neutralizeLegacyOwnitColor, normalizeDataUser } from './sample-data';
import { OwnitSupabaseConfig, getOwnitCloudConfig } from './cloud-config';
import { getStoredSupabaseAccessToken } from './auth';
import { OWNIT_DEFAULT_FONT_ID, normalizeOwnitFontId } from './fonts';

type CollectionMap = {
    users: User[];
    calendars: Calendar[];
    groups: Group[];
    events: Event[];
    todos: Todo[];
    todoCategories: TodoCategory[];
    todoGoals: TodoGoal[];
    todoRoutines: TodoRoutine[];
    todoDailyRecords: TodoDailyRecord[];
    todoCompletionRecords: TodoCompletionRecord[];
    todoPostponeRecords: TodoPostponeRecord[];
    todoTimerSessions: TodoTimerSession[];
    todoDiaries: TodoDiaryEntry[];
    todoReactions: TodoReaction[];
    stickerAssets: StickerAsset[];
    decorations: Decoration[];
    themes: Theme[];
};

type CollectionName = keyof CollectionMap;
type CollectionRecord<K extends CollectionName> = CollectionMap[K][number];

export type RepositoryAdapterKind = 'localStorage' | 'supabase' | 'firebase';

export interface RepositoryAdapterInfo {
    kind: RepositoryAdapterKind;
    name: string;
    currentVersion: number;
    supportsRealtime: boolean;
    supportsOffline: boolean;
}

export interface RepositorySyncStatus extends RepositoryAdapterInfo {
    storageKey: string;
    storedVersion: number;
    migrated: boolean;
    updatedAt: string;
    pendingLocalChanges: boolean;
}

export interface CloudRepositoryStatus {
    enabled: boolean;
    provider: RepositoryAdapterKind;
    label: string;
    state: 'local' | 'idle' | 'syncing' | 'error';
    message: string;
    lastSyncedAt: string;
    lastCheckedAt: string;
    lastError: string;
    pendingWrites: boolean;
    pushedRecords: number;
    skippedStaleWrites: number;
}

export interface CloudImportResult {
    importedAt: string;
    pushedRecords: number;
    pulledRecords: number;
    skippedStaleWrites: number;
}

export class RepositoryPermissionError extends Error {
    public readonly status: number;

    constructor(message: string, status = 403) {
        super(message);
        this.name = 'RepositoryPermissionError';
        this.status = status;
    }
}

export function isRepositoryPermissionError(error: unknown): error is RepositoryPermissionError {
    return error instanceof RepositoryPermissionError || (error instanceof Error && error.name === 'RepositoryPermissionError');
}

export function repositoryErrorMessage(error: unknown, fallback: string): string {
    if (isRepositoryPermissionError(error)) return error.message || '권한이 없어 요청을 완료하지 못했습니다.';
    return error instanceof Error && error.message ? error.message : fallback;
}

export interface SyncRepository {
    loadForUser(user: User): OwnitDataSnapshot;
    getSnapshotForUser(userId: string): OwnitDataSnapshot;
    getAdapterInfo(): RepositoryAdapterInfo;
    getSyncStatus(): RepositorySyncStatus;
    getCloudStatus?(): CloudRepositoryStatus;
    getStorageVersion(): number;
    migrate(): OwnitPersistedState;
    exportSnapshotForCloud?(user: User): OwnitDataSnapshot;
    importSnapshotFromCloud?(user: User, snapshot: Partial<OwnitDataSnapshot>): OwnitDataSnapshot;
    exportBackup?(user: User): OwnitBackupEnvelope;
    importBackup?(user: User, backup: OwnitBackupEnvelope): OwnitDataSnapshot;
    refreshFromCloud?(user: User): Promise<OwnitDataSnapshot>;
    importLocalDataToCloud?(user: User): Promise<CloudImportResult>;
    flushPendingWrites?(): Promise<void>;
    loadLocalState<T>(key: string, fallback: T): T;
    saveLocalState<T>(key: string, value: T): void;
}

export interface UserRepository {
    listUsers(): User[];
    createUser(record: User): User;
    updateUser(id: string, patch: Partial<User>): User | null;
    deleteUser(id: string): boolean;
    createGroup(record: Group): Group;
    updateGroup(id: string, patch: Partial<Group>): Group | null;
    joinGroupByInviteCode(user: User, inviteCode: string): Group | null;
    deleteGroup(id: string): boolean;
}

export interface CalendarRepository {
    listByUser(userId: string): Calendar[];
    listByGroup(groupId: string): Calendar[];
    listCalendarsByUser(userId: string): Calendar[];
    listCalendarsByGroup(groupId: string): Calendar[];
    createCalendar(record: Calendar): Calendar;
    updateCalendar(id: string, patch: Partial<Calendar>): Calendar | null;
    archiveCalendar(id: string): Calendar | null;
    restoreCalendar(id: string): Calendar | null;
    deleteCalendar(id: string): boolean;
    createEvent(record: Event): Event;
    updateEvent(id: string, patch: Partial<Event>): Event | null;
    deleteEvent(id: string): boolean;
}

export type TodoCategoryDeleteResolution = 'uncategorized' | 'delete-todos' | 'move';

export interface TodoGoalDeleteResolutionRequest {
    goalId: string;
    mode: TodoCategoryDeleteResolution;
    targetGoalId?: string | null;
    uncategorizedLabel?: string;
    uncategorizedColor?: string;
    routineFallbackLabel?: string;
    routineFallbackColor?: string;
}

export interface TodoGoalDeleteResult {
    deletedGoalId: string;
    deletedCategoryId: string | null;
    mode: TodoCategoryDeleteResolution;
    targetGoalId: string | null;
    updatedTodoIds: string[];
    deletedTodoIds: string[];
    updatedRoutineIds: string[];
    deletedRoutineIds: string[];
}

export interface TodoRepository {
    createTodo(record: Todo): Todo;
    updateTodo(id: string, patch: Partial<Todo>): Todo | null;
    deleteTodo(id: string): boolean;
    createTodoTimerSession(record: TodoTimerSession): TodoTimerSession;
    createTodoCategory(record: TodoCategory): TodoCategory;
    updateTodoCategory(id: string, patch: Partial<TodoCategory>): TodoCategory | null;
    deleteTodoCategory(id: string): boolean;
    createTodoGoal(record: TodoGoal): TodoGoal;
    updateTodoGoal(id: string, patch: Partial<TodoGoal>): TodoGoal | null;
    deleteTodoGoal(id: string): boolean;
    deleteTodoGoalWithResolution(request: TodoGoalDeleteResolutionRequest): TodoGoalDeleteResult | null;
    createTodoRoutine(record: TodoRoutine): TodoRoutine;
    updateTodoRoutine(id: string, patch: Partial<TodoRoutine>): TodoRoutine | null;
    deleteTodoRoutine(id: string): boolean;
    upsertTodoCompletionRecord(record: TodoCompletionRecord): TodoCompletionRecord;
    deleteTodoCompletionRecord(id: string): boolean;
    createTodoPostponeRecord(record: TodoPostponeRecord): TodoPostponeRecord;
    createTodoDailyRecord(record: TodoDailyRecord): TodoDailyRecord;
    updateTodoDailyRecord(id: string, patch: Partial<TodoDailyRecord>): TodoDailyRecord | null;
    deleteTodoDailyRecord(id: string): boolean;
    createTodoReaction(record: TodoReaction): TodoReaction;
    updateTodoReaction(id: string, patch: Partial<TodoReaction>): TodoReaction | null;
    deleteTodoReaction(id: string): boolean;
}

export interface ThemeRepository {
    createStickerAsset(record: StickerAsset): StickerAsset;
    updateStickerAsset(id: string, patch: Partial<StickerAsset>): StickerAsset | null;
    deleteStickerAsset(id: string): boolean;
    createDecoration(record: Decoration): Decoration;
    updateDecoration(id: string, patch: Partial<Decoration>): Decoration | null;
    deleteDecoration(id: string): boolean;
    createTheme(record: Theme): Theme;
    updateTheme(id: string, patch: Partial<Theme>): Theme | null;
    deleteTheme(id: string): boolean;
}

export interface OwnitDataRepository extends CalendarRepository, TodoRepository, UserRepository, ThemeRepository, SyncRepository { }

export interface OwnitRepositoryAdapter extends OwnitDataRepository { }

export function createLocalStorageRepositoryAdapter(): OwnitRepositoryAdapter {
    return new OwnitLocalStorageRepository();
}

export function createOwnitRepositoryAdapter(): OwnitRepositoryAdapter {
    const cloudConfig = getOwnitCloudConfig();
    if (cloudConfig?.provider === 'supabase') {
        return new OwnitSupabaseRepository(cloudConfig);
    }
    return createLocalStorageRepositoryAdapter();
}

export class OwnitLocalStorageRepository implements OwnitRepositoryAdapter {
    protected readonly storageKey = 'ownit.calendar.data.v1';
    private readonly demoModeKey = 'ownit.demoMode.v1';
    private readonly sampleEventSuffixes = [
        'weekly-routine',
        'month-review',
        'center-briefing',
        'theme-day',
        'center-workshop'
    ];
    private readonly sampleTodoSuffixes = [
        'plan',
        'share',
        'receipt'
    ];
    private readonly sampleTodoCategorySuffixes = [
        'plan',
        'routine',
        'record'
    ];
    private readonly sampleTodoGoalSuffixes = [
        'daily-plan',
        'routine'
    ];
    private readonly sampleTodoRoutineSuffixes = [
        'morning-plan'
    ];
    private readonly sampleStickerSuffixes = [
        'star',
        'ribbon',
        'sun',
        'party'
    ];
    private readonly sampleCalendarSuffixes = [
        'center'
    ];
    private readonly sampleGroupSuffixes = [
        'center'
    ];
    private activeUser: User | null = null;

    public getAdapterInfo(): RepositoryAdapterInfo {
        return {
            kind: 'localStorage',
            name: 'Ownit localStorage repository',
            currentVersion: OWNIT_DATA_VERSION,
            supportsRealtime: false,
            supportsOffline: true
        };
    }

    public getSyncStatus(): RepositorySyncStatus {
        const state = this.readState();
        const storedVersion = this.getStorageVersion();
        return {
            ...this.getAdapterInfo(),
            storageKey: this.storageKey,
            storedVersion,
            migrated: storedVersion === OWNIT_DATA_VERSION,
            updatedAt: state.updatedAt,
            pendingLocalChanges: false
        };
    }

    public getCloudStatus(): CloudRepositoryStatus {
        return {
            enabled: false,
            provider: 'localStorage',
            label: 'localStorage',
            state: 'local',
            message: '클라우드 환경변수가 없어 로컬 저장소로 동작 중입니다.',
            lastSyncedAt: '',
            lastCheckedAt: '',
            lastError: '',
            pendingWrites: false,
            pushedRecords: 0,
            skippedStaleWrites: 0
        };
    }

    public getStorageVersion(): number {
        try {
            const raw = this.storage.getItem(this.storageKey);
            if (!raw) return 0;
            const parsed = JSON.parse(raw) as Partial<OwnitPersistedState>;
            return Number(parsed.version) || 0;
        } catch (error) {
            return 0;
        }
    }

    public migrate(): OwnitPersistedState {
        const state = this.readState();
        this.writeState(state);
        return state;
    }

    public exportSnapshotForCloud(user: User): OwnitDataSnapshot {
        const normalizedUser = normalizeDataUser(user);
        this.activeUser = normalizedUser;
        const state = this.ensureSeeded(normalizedUser);
        return this.createSnapshot(state, normalizedUser.id, normalizedUser, true);
    }

    public importSnapshotFromCloud(user: User, snapshot: Partial<OwnitDataSnapshot>): OwnitDataSnapshot {
        const normalizedUser = normalizeDataUser(user);
        this.activeUser = normalizedUser;
        const state = this.readState();
        this.mergeRecordInState(state, 'users', normalizeDataUser(snapshot.user || normalizedUser));
        this.mergeRecordsInState(state, 'calendars', snapshot.calendars);
        this.mergeRecordsInState(state, 'groups', snapshot.groups);
        this.mergeRecordsInState(state, 'events', snapshot.events);
        this.mergeRecordsInState(state, 'todos', snapshot.todos);
        this.mergeRecordsInState(state, 'todoCategories', snapshot.todoCategories);
        this.mergeRecordsInState(state, 'todoGoals', snapshot.todoGoals);
        this.mergeRecordsInState(state, 'todoRoutines', snapshot.todoRoutines);
        this.mergeRecordsInState(state, 'todoDailyRecords', snapshot.todoDailyRecords);
        this.mergeRecordsInState(state, 'todoCompletionRecords', snapshot.todoCompletionRecords);
        this.mergeRecordsInState(state, 'todoPostponeRecords', snapshot.todoPostponeRecords);
        this.mergeRecordsInState(state, 'todoTimerSessions', snapshot.todoTimerSessions);
        this.mergeRecordsInState(state, 'todoDiaries', snapshot.todoDiaries);
        this.mergeRecordsInState(state, 'todoReactions', snapshot.todoReactions);
        this.mergeRecordsInState(state, 'stickerAssets', snapshot.stickerAssets);
        this.mergeRecordsInState(state, 'decorations', snapshot.decorations);
        this.mergeRecordsInState(state, 'themes', snapshot.themes);
        state.themes = this.withDefaultThemes(state.themes);
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.ensureDefaultWorkspace(state, normalizedUser);
        this.writeState(state);
        return this.createSnapshot(state, normalizedUser.id, normalizedUser);
    }

    public exportBackup(user: User): OwnitBackupEnvelope {
        const normalizedUser = normalizeDataUser(user);
        const snapshot = this.exportSnapshotForCloud(normalizedUser);
        const exportedAt = new Date().toISOString();
        return {
            manifest: {
                schemaVersion: OWNIT_DATA_VERSION,
                exportedAt,
                exportedBy: normalizedUser.id,
                adapterKind: this.getAdapterInfo().kind,
                scope: 'user',
                userNamespace: this.userNamespace(normalizedUser.id),
                sharedNamespaces: snapshot.groups.map((group) => this.groupNamespace(group.id)),
                collections: this.countBackupCollections(snapshot),
                notes: [
                    '개인 데이터는 ownerId/userId/authorId 기준으로 현재 사용자 snapshot만 포함합니다.',
                    '공유 데이터는 현재 사용자가 볼 수 있는 groupId/calendarId 범위만 포함합니다.',
                    'Supabase/Firebase 전환 시 이 envelope를 adapter import 입력으로 사용할 수 있습니다.'
                ]
            },
            snapshot
        };
    }

    public importBackup(user: User, backup: OwnitBackupEnvelope): OwnitDataSnapshot {
        const normalizedUser = normalizeDataUser(user);
        const backupUser = backup?.snapshot?.user ? normalizeDataUser(backup.snapshot.user) : normalizedUser;
        if (!this.isAdminDataUser(normalizedUser) && backupUser.id !== normalizedUser.id) {
            throw new Error('다른 사용자의 백업은 현재 계정으로 가져올 수 없습니다.');
        }
        return this.importSnapshotFromCloud(normalizedUser, backup.snapshot);
    }

    public loadLocalState<T>(key: string, fallback: T): T {
        try {
            return this.readJson(key, fallback);
        } catch (error) {
            return fallback;
        }
    }

    public saveLocalState<T>(key: string, value: T): void {
        try {
            this.writeJson(key, value);
        } catch (error) {
            // Local UI state must not block foreground data actions.
        }
    }

    public loadForUser(user: User): OwnitDataSnapshot {
        const normalizedUser = normalizeDataUser(user);
        this.activeUser = normalizedUser;
        const state = this.ensureSeeded(normalizedUser);
        return this.createSnapshot(state, normalizedUser.id, normalizedUser);
    }

    public getSnapshotForUser(userId: string): OwnitDataSnapshot {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const id = userId || actor.id;
        if (!this.isAdminDataUser(actor) && id !== actor.id) {
            return this.createSnapshot(state, actor.id, actor);
        }
        const user = state.users.find((item) => item.id === id) || actor;
        return this.createSnapshot(state, id, user);
    }

    public listUsers(): User[] {
        return this.readState().users.filter((user) => !this.isDeletedRecord(user));
    }

    public createUser(record: User): User {
        return this.createRecord('users', record);
    }

    public updateUser(id: string, patch: Partial<User>): User | null {
        return this.updateRecord('users', id, patch);
    }

    public deleteUser(id: string): boolean {
        return this.deleteRecord('users', id);
    }

    public listByUser(userId: string): Calendar[] {
        return this.listCalendarsByUser(userId);
    }

    public listByGroup(groupId: string): Calendar[] {
        return this.listCalendarsByGroup(groupId);
    }

    public listCalendarsByUser(userId: string): Calendar[] {
        const id = userId || DEFAULT_GUEST_USER_ID;
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        if (!this.isAdminDataUser(actor) && actor.id !== id) return [];
        return this.sortCalendars(this.readState().calendars
            .map((calendar, index) => this.normalizeCalendarDefaults(calendar, index))
            .filter((calendar) => this.isActiveCalendar(calendar) && calendar.ownerId === id));
    }

    public listCalendarsByGroup(groupId: string): Calendar[] {
        if (!groupId) return [];
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const group = state.groups
            .map((item) => this.normalizeGroupDefaults(item))
            .find((item) => item.id === groupId && this.isActiveGroup(item)) || null;
        if (!group || (!this.isAdminDataUser(actor) && !canViewGroup(actor, group))) return [];
        return this.sortCalendars(state.calendars
            .map((calendar, index) => this.normalizeCalendarDefaults(calendar, index))
            .filter((calendar) => this.isActiveCalendar(calendar) && calendar.groupId === groupId));
    }

    public createCalendar(record: Calendar): Calendar {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const calendar = this.withSyncMetadata(
            this.normalizeCalendarDefaults(record, this.nextCalendarOrder(state.calendars, record.ownerId, record.type)),
            record.ownerId || actor.id,
            record.groupId || null
        );
        this.assertCanCreateCalendar(actor, calendar, state);
        if (state.calendars.some((item) => item.id === calendar.id)) {
            throw new Error(`Duplicate calendars id: ${calendar.id}`);
        }

        state.calendars.push(calendar);
        this.writeState(state);
        return calendar;
    }

    public updateCalendar(id: string, patch: Partial<Calendar>): Calendar | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.calendars.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeCalendarDefaults(state.calendars[index], index);
        this.assertCanManageCalendar(actor, existing, state);
        const now = new Date().toISOString();
        const updated = this.normalizeCalendarDefaults({
            ...existing,
            ...patch,
            id: existing.id,
            ownerId: existing.isDefault ? existing.ownerId : (patch.ownerId || existing.ownerId),
            type: existing.isDefault ? 'personal' : (patch.type || existing.type),
            groupId: existing.isDefault ? null : (patch.groupId ?? existing.groupId),
            isDefault: existing.isDefault,
            archivedAt: existing.isDefault ? null : (patch.archivedAt ?? existing.archivedAt),
            deletedAt: existing.isDefault ? null : (patch.deletedAt ?? existing.deletedAt),
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion),
            localUpdatedAt: patch.localUpdatedAt || patch.updatedAt || now,
            updatedAt: patch.updatedAt || now
        }, existing.order);
        this.assertCanManageCalendar(actor, updated, state);

        state.calendars[index] = updated;
        if (existing.type !== updated.type || existing.groupId !== updated.groupId) {
            state.events = state.events.map((event) => {
                if (event.calendarId !== updated.id) return event;
                return {
                    ...event,
                    groupId: updated.groupId || null,
                    visibility: updated.type === 'shared' ? 'group' : 'private',
                    updatedAt: now,
                    localUpdatedAt: now,
                    syncVersion: this.nextSyncVersion(event)
                };
            });
        }
        this.writeState(state);
        return updated;
    }

    public archiveCalendar(id: string): Calendar | null {
        return this.updateCalendarStatus(id, { archivedAt: new Date().toISOString(), deletedAt: null });
    }

    public restoreCalendar(id: string): Calendar | null {
        return this.updateCalendarStatus(id, { archivedAt: null, deletedAt: null });
    }

    public deleteCalendar(id: string): boolean {
        return Boolean(this.updateCalendarStatus(id, { deletedAt: new Date().toISOString() }));
    }

    public createEvent(record: Event): Event {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const calendar = this.calendarInState(state, record.calendarId);
        this.assertCanManageEvent(actor, null, calendar, state);
        if (!this.isAdminDataUser(actor) && record.authorId !== actor.id) {
            throw new Error('본인 명의의 일정만 생성할 수 있습니다.');
        }
        if (!calendar) throw new Error('일정을 추가할 캘린더를 찾을 수 없습니다.');
        const now = new Date().toISOString();
        const event = this.withSyncMetadata({
            ...record,
            authorId: this.isAdminDataUser(actor) ? record.authorId : actor.id,
            visibility: calendar.type === 'shared' ? 'group' as const : 'private' as const,
            localUpdatedAt: record.localUpdatedAt || record.updatedAt || now,
            syncStatus: record.syncStatus || 'idle',
            syncError: record.syncError || null
        }, record.authorId || actor.id, calendar.groupId || null);
        this.assertEventCalendarConsistency(actor, event, calendar, state);
        if (state.events.some((item) => item.id === event.id)) {
            throw new Error(`Duplicate events id: ${event.id}`);
        }
        state.events.push(event);
        this.writeState(state);
        return event;
    }

    public updateEvent(id: string, patch: Partial<Event>): Event | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.events.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = state.events[index];
        const existingCalendar = this.calendarInState(state, existing.calendarId);
        this.assertCanManageEvent(actor, existing, existingCalendar, state);

        const now = new Date().toISOString();
        const updated = {
            ...existing,
            ...patch,
            id: existing.id,
            updatedAt: patch.updatedAt || now,
            localUpdatedAt: patch.localUpdatedAt || patch.updatedAt || now,
            syncStatus: patch.syncStatus || existing.syncStatus || 'idle',
            syncError: patch.syncError === undefined ? existing.syncError || null : patch.syncError,
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion)
        } as Event;
        if (!this.isAdminDataUser(actor)) {
            updated.authorId = existing.authorId;
        }
        const targetCalendar = this.calendarInState(state, updated.calendarId);
        this.assertCanMoveEventBetweenCalendars(actor, existingCalendar, targetCalendar, state);
        this.assertCanManageEvent(actor, existing, targetCalendar, state);
        if (!targetCalendar) throw new Error('일정을 이동할 캘린더를 찾을 수 없습니다.');
        updated.visibility = targetCalendar.type === 'shared' ? 'group' : 'private';
        updated.groupId = targetCalendar.groupId || null;
        this.assertEventCalendarConsistency(actor, updated, targetCalendar, state);

        state.events[index] = updated;
        this.writeState(state);
        return updated;
    }

    public deleteEvent(id: string): boolean {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.events.findIndex((item) => item.id === id);
        if (index < 0) return false;

        const event = state.events[index];
        this.assertCanManageEvent(actor, event, this.calendarInState(state, event.calendarId), state);
        state.events[index] = this.softDeleteRecord(event);
        this.writeState(state);
        return true;
    }

    public createTodo(record: Todo): Todo {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const todo = this.withSyncMetadata(
            this.normalizeTodoDefaults(record, this.nextTodoOrder(state.todos, record)),
            record.ownerId || record.createdBy || record.authorId || actor.id,
            record.groupId || null
        );
        if (!this.isAdminDataUser(actor) && !this.isOwnTodoRecord(actor.id, todo)) {
            throw new Error('본인 Todo만 생성할 수 있습니다.');
        }
        if (state.todos.some((item) => item.id === todo.id)) {
            throw new Error(`Duplicate todos id: ${todo.id}`);
        }
        state.todos.push(todo);
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return todo;
    }

    public updateTodo(id: string, patch: Partial<Todo>): Todo | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todos.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeTodoDefaults(state.todos[index], state.todos[index].order);
        this.assertCanManageTodo(actor, existing);
        const updated = this.normalizeTodoDefaults({
            ...existing,
            ...patch,
            updatedAt: patch.updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion)
        }, existing.order);
        if (!this.isAdminDataUser(actor) && !this.isOwnTodoRecord(actor.id, updated)) {
            throw new Error('본인 Todo로만 저장할 수 있습니다.');
        }
        state.todos[index] = updated;
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return updated;
    }

    public deleteTodo(id: string): boolean {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todos.findIndex((item) => item.id === id);
        if (index < 0) return false;

        this.assertCanManageTodo(actor, this.normalizeTodoDefaults(state.todos[index], state.todos[index].order));
        state.todos[index] = this.softDeleteRecord(state.todos[index]);
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return true;
    }

    public createTodoTimerSession(record: TodoTimerSession): TodoTimerSession {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const normalizedSession = this.normalizeTodoTimerSessionDefaults(record);
        const sourceTodo = state.todos.find((item) => item.id === normalizedSession.todoId);
        if (!sourceTodo) throw new Error('타이머를 기록할 Todo를 찾을 수 없습니다.');
        const session = this.withSyncMetadata(
            normalizedSession,
            record.ownerId || sourceTodo.ownerId || sourceTodo.createdBy || sourceTodo.authorId || actor.id,
            record.groupId || sourceTodo.groupId || null
        );
        if (state.todoTimerSessions.some((item) => item.id === session.id)) {
            throw new Error(`Duplicate todoTimerSessions id: ${session.id}`);
        }
        this.assertCanManageTodo(actor, this.normalizeTodoDefaults(sourceTodo, sourceTodo.order));

        state.todoTimerSessions.push(session);
        const todoIndex = state.todos.findIndex((item) => item.id === session.todoId);
        if (todoIndex >= 0) {
            const todo = state.todos[todoIndex];
            const timerSessionIds = Array.from(new Set([...(todo.timerSessionIds || []), session.id]));
            const timerSeconds = this.totalCompletedTimerSecondsForTodo(state, session.todoId);
            state.todos[todoIndex] = this.normalizeTodoDefaults({
                ...todo,
                timerSessionIds,
                timerSeconds,
                updatedAt: session.updatedAt
            }, todo.order);
        }

        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return session;
    }

    public createTodoCategory(record: TodoCategory): TodoCategory {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const category = this.withSyncMetadata(this.normalizeTodoCategoryDefaults({
            ...record,
            userId: this.isAdminDataUser(actor) ? (record.userId || record.ownerId || actor.id) : actor.id,
            ownerId: this.isAdminDataUser(actor) ? (record.ownerId || record.userId || actor.id) : actor.id
        }), record.ownerId || record.userId || actor.id, record.groupId || null);
        this.assertCanManageTodoCategory(actor, category);
        if (state.todoCategories.some((item) => item.id === category.id)) {
            throw new Error(`Duplicate todoCategories id: ${category.id}`);
        }
        state.todoCategories.push(category);
        this.writeState(state);
        return category;
    }

    public updateTodoCategory(id: string, patch: Partial<TodoCategory>): TodoCategory | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todoCategories.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeTodoCategoryDefaults(state.todoCategories[index], state.todoCategories[index].order);
        this.assertCanManageTodoCategory(actor, existing);
        const updated = this.normalizeTodoCategoryDefaults({
            ...existing,
            ...patch,
            id: existing.id,
            userId: this.isAdminDataUser(actor) ? (patch.userId || existing.userId) : existing.userId,
            ownerId: this.isAdminDataUser(actor) ? (patch.ownerId || existing.ownerId) : existing.ownerId,
            updatedAt: patch.updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion)
        }, existing.order);
        this.assertCanManageTodoCategory(actor, updated);
        state.todoCategories[index] = updated;
        this.writeState(state);
        return updated;
    }

    public deleteTodoCategory(id: string): boolean {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todoCategories.findIndex((item) => item.id === id);
        if (index < 0) return false;

        const category = this.normalizeTodoCategoryDefaults(state.todoCategories[index], state.todoCategories[index].order);
        this.assertCanManageTodoCategory(actor, category);
        const now = new Date().toISOString();
        state.todoCategories[index] = this.softDeleteRecord(category);
        const deletedGoalIds = new Set(state.todoGoals
            .filter((goal) => goal.categoryId === id && !this.isDeletedRecord(goal))
            .map((goal) => goal.id));

        state.todoGoals = state.todoGoals.map((goal, goalIndex) => {
            if (goal.categoryId !== id) return goal;
            return this.softDeleteRecord(this.normalizeTodoGoalDefaults(goal, goal.order ?? goalIndex));
        });
        state.todos = state.todos.map((todo, todoIndex) => {
            if (todo.categoryId !== id && !deletedGoalIds.has(todo.goalId || '')) return todo;
            return this.normalizeTodoDefaults({
                ...todo,
                goalId: null,
                categoryId: null,
                category: '미분류',
                color: '#6B6259',
                updatedAt: now,
                syncVersion: this.nextSyncVersion(todo)
            }, todo.order ?? todoIndex);
        });
        state.todoRoutines = state.todoRoutines.map((routine) => {
            if (routine.categoryId !== id && !deletedGoalIds.has(routine.goalId || '')) return routine;
            return this.normalizeTodoRoutineDefaults({
                ...routine,
                goalId: null,
                categoryId: null,
                memo: '루틴',
                color: '#B59A5C',
                updatedAt: now,
                syncVersion: this.nextSyncVersion(routine)
            });
        });
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return true;
    }

    public createTodoGoal(record: TodoGoal): TodoGoal {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const goal = this.withSyncMetadata(this.normalizeTodoGoalDefaults({
            ...record,
            userId: this.isAdminDataUser(actor) ? (record.userId || record.ownerId || actor.id) : actor.id,
            ownerId: this.isAdminDataUser(actor) ? (record.ownerId || record.userId || actor.id) : actor.id
        }), record.ownerId || record.userId || actor.id, record.groupId || null);
        const category = goal.categoryId
            ? state.todoCategories.map((item, index) => this.normalizeTodoCategoryDefaults(item, item.order ?? index)).find((item) => item.id === goal.categoryId) || null
            : null;
        this.assertCanManageTodoGoal(actor, goal, category);
        if (state.todoGoals.some((item) => item.id === goal.id)) {
            throw new Error(`Duplicate todoGoals id: ${goal.id}`);
        }
        state.todoGoals.push(goal);
        this.writeState(state);
        return goal;
    }

    public updateTodoGoal(id: string, patch: Partial<TodoGoal>): TodoGoal | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todoGoals.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeTodoGoalDefaults(state.todoGoals[index], state.todoGoals[index].order);
        const existingCategory = existing.categoryId
            ? state.todoCategories.map((item, categoryIndex) => this.normalizeTodoCategoryDefaults(item, item.order ?? categoryIndex)).find((item) => item.id === existing.categoryId) || null
            : null;
        this.assertCanManageTodoGoal(actor, existing, existingCategory);
        const updated = this.normalizeTodoGoalDefaults({
            ...existing,
            ...patch,
            id: existing.id,
            userId: this.isAdminDataUser(actor) ? (patch.userId || existing.userId) : existing.userId,
            ownerId: this.isAdminDataUser(actor) ? (patch.ownerId || existing.ownerId) : existing.ownerId,
            updatedAt: patch.updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion)
        }, existing.order);
        const updatedCategory = updated.categoryId
            ? state.todoCategories.map((item, categoryIndex) => this.normalizeTodoCategoryDefaults(item, item.order ?? categoryIndex)).find((item) => item.id === updated.categoryId) || null
            : null;
        this.assertCanManageTodoGoal(actor, updated, updatedCategory);
        state.todoGoals[index] = updated;
        this.writeState(state);
        return updated;
    }

    public deleteTodoGoal(id: string): boolean {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todoGoals.findIndex((item) => item.id === id);
        if (index < 0) return false;

        const goal = this.normalizeTodoGoalDefaults(state.todoGoals[index], state.todoGoals[index].order);
        const category = goal.categoryId
            ? state.todoCategories.map((item, categoryIndex) => this.normalizeTodoCategoryDefaults(item, item.order ?? categoryIndex)).find((item) => item.id === goal.categoryId) || null
            : null;
        this.assertCanManageTodoGoal(actor, goal, category);
        const now = new Date().toISOString();
        state.todoGoals[index] = this.softDeleteRecord(goal);
        state.todos = state.todos.map((todo, todoIndex) => {
            if (todo.goalId !== id) return todo;
            return this.normalizeTodoDefaults({
                ...todo,
                goalId: null,
                updatedAt: now,
                syncVersion: this.nextSyncVersion(todo)
            }, todo.order ?? todoIndex);
        });
        state.todoRoutines = state.todoRoutines.map((routine) => {
            if (routine.goalId !== id) return routine;
            return this.normalizeTodoRoutineDefaults({
                ...routine,
                goalId: null,
                updatedAt: now,
                syncVersion: this.nextSyncVersion(routine)
            });
        });
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return true;
    }

    public deleteTodoGoalWithResolution(request: TodoGoalDeleteResolutionRequest): TodoGoalDeleteResult | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const mode = this.normalizeTodoCategoryDeleteResolution(request.mode);
        const goalIndex = state.todoGoals.findIndex((item) => item.id === request.goalId && !this.isDeletedRecord(item));
        if (goalIndex < 0) return null;

        const now = new Date().toISOString();
        const goal = this.normalizeTodoGoalDefaults(state.todoGoals[goalIndex], state.todoGoals[goalIndex].order ?? goalIndex);
        const categoryIndex = goal.categoryId
            ? state.todoCategories.findIndex((item) => item.id === goal.categoryId && !this.isDeletedRecord(item))
            : -1;
        const category = categoryIndex >= 0
            ? this.normalizeTodoCategoryDefaults(state.todoCategories[categoryIndex], state.todoCategories[categoryIndex].order ?? categoryIndex)
            : null;
        this.assertCanManageTodoGoal(actor, goal, category);
        if (goal.locked || category?.locked) {
            throw new Error('잠긴 카테고리는 삭제할 수 없습니다.');
        }

        let targetGoal: TodoGoal | null = null;
        let targetCategory: TodoCategory | null = null;
        if (mode === 'move') {
            if (!request.targetGoalId || request.targetGoalId === goal.id) {
                throw new Error('이동할 다른 카테고리를 선택해 주세요.');
            }
            const targetGoalIndex = state.todoGoals.findIndex((item) => item.id === request.targetGoalId && !this.isDeletedRecord(item));
            if (targetGoalIndex < 0) throw new Error('이동할 카테고리를 찾지 못했습니다.');
            targetGoal = this.normalizeTodoGoalDefaults(state.todoGoals[targetGoalIndex], state.todoGoals[targetGoalIndex].order ?? targetGoalIndex);
            const targetCategoryIndex = targetGoal.categoryId
                ? state.todoCategories.findIndex((item) => item.id === targetGoal!.categoryId && !this.isDeletedRecord(item))
                : -1;
            targetCategory = targetCategoryIndex >= 0
                ? this.normalizeTodoCategoryDefaults(state.todoCategories[targetCategoryIndex], state.todoCategories[targetCategoryIndex].order ?? targetCategoryIndex)
                : null;
            if (targetGoal.categoryId && !targetCategory) throw new Error('이동할 카테고리 정보를 찾지 못했습니다.');
            if (targetGoal.locked || targetCategory?.locked) throw new Error('잠긴 카테고리로는 이동할 수 없습니다.');
            if (category?.id && targetCategory?.id === category.id) {
                throw new Error('같은 카테고리로는 이동할 수 없습니다.');
            }
            this.assertCanManageTodoGoal(actor, targetGoal, targetCategory);
        }

        const affectedTodoIndexes = state.todos
            .map((todo, index) => ({ todo: this.normalizeTodoDefaults(todo, todo.order ?? index), index }))
            .filter(({ todo }) => !this.isDeletedRecord(todo) && this.todoMatchesGoalCategory(todo, goal, category));
        const affectedRoutineIndexes = state.todoRoutines
            .map((routine, index) => ({ routine: this.normalizeTodoRoutineDefaults(routine), index }))
            .filter(({ routine }) => !this.isDeletedRecord(routine) && this.routineMatchesGoalCategory(routine, goal, category));

        for (const { todo } of affectedTodoIndexes) this.assertCanManageTodo(actor, todo);
        for (const { routine } of affectedRoutineIndexes) this.assertCanManageTodoRoutine(actor, routine);

        const result: TodoGoalDeleteResult = {
            deletedGoalId: goal.id,
            deletedCategoryId: category?.id || null,
            mode,
            targetGoalId: targetGoal?.id || null,
            updatedTodoIds: [],
            deletedTodoIds: [],
            updatedRoutineIds: [],
            deletedRoutineIds: []
        };
        const uncategorizedLabel = (request.uncategorizedLabel || '미분류').trim() || '미분류';
        const uncategorizedColor = neutralizeLegacyOwnitColor(request.uncategorizedColor || '#6B6259');
        const routineFallbackLabel = (request.routineFallbackLabel || uncategorizedLabel || '루틴').trim() || '루틴';
        const routineFallbackColor = neutralizeLegacyOwnitColor(request.routineFallbackColor || uncategorizedColor || '#B59A5C');

        for (const { todo, index } of affectedTodoIndexes) {
            if (mode === 'delete-todos') {
                state.todos[index] = this.softDeleteRecord(todo);
                result.deletedTodoIds.push(todo.id);
                continue;
            }
            state.todos[index] = this.normalizeTodoDefaults({
                ...todo,
                goalId: targetGoal?.id || null,
                categoryId: targetCategory?.id || targetGoal?.categoryId || null,
                category: targetGoal?.name || targetCategory?.name || uncategorizedLabel,
                color: targetGoal?.color || targetCategory?.color || uncategorizedColor,
                updatedAt: now,
                syncVersion: this.nextSyncVersion(todo)
            }, todo.order);
            result.updatedTodoIds.push(todo.id);
        }

        for (const { routine, index } of affectedRoutineIndexes) {
            if (mode === 'delete-todos') {
                state.todoRoutines[index] = this.softDeleteRecord(routine);
                state.todoCompletionRecords = state.todoCompletionRecords.map((record) => (
                    record.routineId === routine.id ? this.softDeleteRecord(record) : record
                ));
                state.todoPostponeRecords = state.todoPostponeRecords.map((record) => (
                    record.routineId === routine.id ? this.softDeleteRecord(record) : record
                ));
                result.deletedRoutineIds.push(routine.id);
                continue;
            }
            state.todoRoutines[index] = this.normalizeTodoRoutineDefaults({
                ...routine,
                goalId: targetGoal?.id || null,
                categoryId: targetCategory?.id || targetGoal?.categoryId || null,
                memo: targetGoal?.name || targetCategory?.name || routineFallbackLabel,
                color: targetGoal?.color || targetCategory?.color || routineFallbackColor,
                updatedAt: now,
                syncVersion: this.nextSyncVersion(routine)
            });
            result.updatedRoutineIds.push(routine.id);
        }

        state.todoGoals[goalIndex] = this.softDeleteRecord(goal);
        if (categoryIndex >= 0 && !this.todoCategoryUsedByActiveGoal(state, category!.id, goal.id)) {
            state.todoCategories[categoryIndex] = this.softDeleteRecord(category!);
        } else {
            result.deletedCategoryId = null;
        }
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return result;
    }

    public createTodoRoutine(record: TodoRoutine): TodoRoutine {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const routine = this.withSyncMetadata(this.normalizeTodoRoutineDefaults({
            ...record,
            userId: this.isAdminDataUser(actor) ? (record.userId || record.ownerId || actor.id) : actor.id,
            ownerId: this.isAdminDataUser(actor) ? (record.ownerId || record.userId || actor.id) : actor.id
        }), record.ownerId || record.userId || actor.id, record.groupId || null);
        this.assertCanManageTodoRoutine(actor, routine);
        if (state.todoRoutines.some((item) => item.id === routine.id)) {
            throw new Error(`Duplicate todoRoutines id: ${routine.id}`);
        }
        state.todoRoutines.push(routine);
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return routine;
    }

    public updateTodoRoutine(id: string, patch: Partial<TodoRoutine>): TodoRoutine | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todoRoutines.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeTodoRoutineDefaults(state.todoRoutines[index]);
        this.assertCanManageTodoRoutine(actor, existing);
        const updated = this.normalizeTodoRoutineDefaults({
            ...existing,
            ...patch,
            id: existing.id,
            userId: this.isAdminDataUser(actor) ? (patch.userId || existing.userId) : existing.userId,
            ownerId: this.isAdminDataUser(actor) ? (patch.ownerId || existing.ownerId) : existing.ownerId,
            updatedAt: patch.updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion)
        });
        this.assertCanManageTodoRoutine(actor, updated);
        state.todoRoutines[index] = updated;
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return updated;
    }

    public deleteTodoRoutine(id: string): boolean {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todoRoutines.findIndex((item) => item.id === id);
        if (index < 0) return false;

        const routine = this.normalizeTodoRoutineDefaults(state.todoRoutines[index]);
        this.assertCanManageTodoRoutine(actor, routine);
        state.todoRoutines[index] = this.softDeleteRecord(routine);
        state.todoCompletionRecords = state.todoCompletionRecords.map((record) => record.routineId === id ? this.softDeleteRecord(record) : record);
        state.todoPostponeRecords = state.todoPostponeRecords.map((record) => record.routineId === id ? this.softDeleteRecord(record) : record);
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return true;
    }

    public upsertTodoCompletionRecord(record: TodoCompletionRecord): TodoCompletionRecord {
        const state = this.readState();
        const completion = this.withSyncMetadata(this.normalizeTodoCompletionRecordDefaults(record), record.ownerId || record.authorId || DEFAULT_GUEST_USER_ID, record.groupId || null);
        const index = state.todoCompletionRecords.findIndex((item) => item.id === completion.id);
        if (index >= 0) {
            state.todoCompletionRecords[index] = completion;
        } else {
            state.todoCompletionRecords.push(completion);
        }
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return completion;
    }

    public deleteTodoCompletionRecord(id: string): boolean {
        const state = this.readState();
        const index = state.todoCompletionRecords.findIndex((item) => item.id === id);
        if (index < 0) return false;

        state.todoCompletionRecords[index] = this.softDeleteRecord(state.todoCompletionRecords[index]);
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        this.writeState(state);
        return true;
    }

    public createTodoPostponeRecord(record: TodoPostponeRecord): TodoPostponeRecord {
        const state = this.readState();
        const postpone = this.normalizeTodoPostponeRecordDefaults(record);
        if (state.todoPostponeRecords.some((item) => item.id === postpone.id)) {
            throw new Error(`Duplicate todoPostponeRecords id: ${postpone.id}`);
        }
        const sourceTodo = postpone.todoId ? state.todos.find((item) => item.id === postpone.todoId) || null : null;
        const sourceRoutine = postpone.routineId ? state.todoRoutines.find((item) => item.id === postpone.routineId) || null : null;
        const created = this.withSyncMetadata(
            postpone,
            record.ownerId
                || sourceTodo?.ownerId
                || sourceTodo?.createdBy
                || sourceTodo?.authorId
                || sourceRoutine?.ownerId
                || sourceRoutine?.userId
                || DEFAULT_GUEST_USER_ID,
            record.groupId || sourceTodo?.groupId || sourceRoutine?.groupId || null
        );
        state.todoPostponeRecords.push(created);
        this.writeState(state);
        return created;
    }

    public createTodoDailyRecord(record: TodoDailyRecord): TodoDailyRecord {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const dailyRecord = this.withSyncMetadata(this.normalizeTodoDailyRecordDefaults({
            ...record,
            userId: this.isAdminDataUser(actor) ? (record.userId || record.ownerId || actor.id) : actor.id
        }), record.ownerId || record.userId || actor.id, record.groupId || null);
        this.assertCanManageTodoDailyRecord(actor, dailyRecord);
        if (state.todoDailyRecords.some((item) => item.id === dailyRecord.id)) {
            throw new Error(`Duplicate todoDailyRecords id: ${dailyRecord.id}`);
        }
        state.todoDailyRecords.push(dailyRecord);
        this.writeState(state);
        return dailyRecord;
    }

    public updateTodoDailyRecord(id: string, patch: Partial<TodoDailyRecord>): TodoDailyRecord | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todoDailyRecords.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeTodoDailyRecordDefaults(state.todoDailyRecords[index]);
        this.assertCanManageTodoDailyRecord(actor, existing);
        const updated = this.normalizeTodoDailyRecordDefaults({
            ...existing,
            ...patch,
            id: existing.id,
            userId: this.isAdminDataUser(actor) ? (patch.userId || existing.userId) : existing.userId,
            updatedAt: patch.updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion)
        });
        this.assertCanManageTodoDailyRecord(actor, updated);
        state.todoDailyRecords[index] = updated;
        this.writeState(state);
        return updated;
    }

    public deleteTodoDailyRecord(id: string): boolean {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.todoDailyRecords.findIndex((item) => item.id === id);
        if (index < 0) return false;

        const record = this.normalizeTodoDailyRecordDefaults(state.todoDailyRecords[index]);
        this.assertCanManageTodoDailyRecord(actor, record);
        state.todoDailyRecords[index] = this.softDeleteRecord(record);
        this.writeState(state);
        return true;
    }

    public createTodoReaction(record: TodoReaction): TodoReaction {
        return this.createRecord('todoReactions', this.normalizeTodoReactionDefaults(record));
    }

    public updateTodoReaction(id: string, patch: Partial<TodoReaction>): TodoReaction | null {
        const state = this.readState();
        const index = state.todoReactions.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const updated = this.normalizeTodoReactionDefaults({
            ...state.todoReactions[index],
            ...patch,
            updatedAt: patch.updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(state.todoReactions[index], patch.syncVersion)
        });
        state.todoReactions[index] = updated;
        this.writeState(state);
        return updated;
    }

    public deleteTodoReaction(id: string): boolean {
        return this.deleteRecord('todoReactions', id);
    }

    public createGroup(record: Group): Group {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const group = this.withSyncMetadata(this.normalizeGroupDefaults(record, actor.id), record.ownerId || record.adminId || actor.id, record.groupId || record.id || null);
        if (!this.isAdminDataUser(actor)) {
            if (group.adminId !== actor.id) throw new Error('본인이 관리자인 공유 그룹만 만들 수 있습니다.');
            const actorMember = group.members.find((member) => member.userId === actor.id && member.status === 'active');
            if (!actorMember || (actorMember.role !== 'owner' && actorMember.role !== 'admin')) {
                throw new Error('공유 그룹 생성자는 owner 또는 admin이어야 합니다.');
            }
        }
        if (state.groups.some((item) => item.id === group.id)) {
            throw new Error(`Duplicate groups id: ${group.id}`);
        }
        state.groups.push(group);
        this.writeState(state);
        return group;
    }

    public updateGroup(id: string, patch: Partial<Group>): Group | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.groups.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeGroupDefaults(state.groups[index]);
        if (!canManageGroup(actor, existing)) {
            throw new Error('공유 그룹을 관리할 권한이 없습니다.');
        }
        const updated = this.normalizeGroupDefaults({
            ...existing,
            ...patch,
            id: existing.id,
            adminId: patch.adminId || existing.adminId,
            members: patch.members || existing.members,
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion),
            updatedAt: patch.updatedAt || new Date().toISOString()
        });
        state.groups[index] = updated;
        this.writeState(state);
        return updated;
    }

    public joinGroupByInviteCode(user: User, inviteCode: string): Group | null {
        const code = (inviteCode || '').trim().toUpperCase();
        if (!code) return null;

        const normalizedUser = normalizeDataUser(user);
        if (this.activeUser && !this.isAdminDataUser(this.activeUser) && this.activeUser.id !== normalizedUser.id) {
            throw new Error('현재 로그인 사용자로만 공유 그룹에 참여할 수 있습니다.');
        }
        this.activeUser = normalizedUser;
        const state = this.readState();
        const index = state.groups.findIndex((item) => {
            const group = this.normalizeGroupDefaults(item, normalizedUser.id);
            return this.isActiveGroup(group) && group.inviteCode.trim().toUpperCase() === code;
        });
        if (index < 0) return null;

        const now = new Date().toISOString();
        const group = this.normalizeGroupDefaults(state.groups[index], normalizedUser.id);
        const memberIndex = group.members.findIndex((member) => member.userId === normalizedUser.id || (member.email && member.email === normalizedUser.email));
        const memberPatch: GroupMember = this.normalizeGroupMemberDefaults({
            id: memberIndex >= 0 ? group.members[memberIndex].id : `member-${group.id}-${normalizedUser.id}`,
            groupId: group.id,
            userId: normalizedUser.id,
            name: normalizedUser.name || normalizedUser.nickname || normalizedUser.username || normalizedUser.email || '나',
            nickname: normalizedUser.nickname || normalizedUser.name || normalizedUser.username || '나',
            email: normalizedUser.email,
            role: memberIndex >= 0 ? group.members[memberIndex].role : 'member',
            status: 'active',
            joinedAt: memberIndex >= 0 ? group.members[memberIndex].joinedAt : now
        }, group.id, group.adminId, now, group.members.length);

        if (memberIndex >= 0) {
            group.members[memberIndex] = {
                ...group.members[memberIndex],
                ...memberPatch,
                status: 'active',
                joinedAt: group.members[memberIndex].joinedAt || now
            };
        } else {
            group.members.push(memberPatch);
        }

        const updated = this.normalizeGroupDefaults({
            ...group,
            updatedAt: now
        }, normalizedUser.id);
        state.groups[index] = updated;
        this.upsertInState(state, 'users', normalizedUser);
        this.writeState(state);
        return updated;
    }

    public deleteGroup(id: string): boolean {
        return Boolean(this.updateGroup(id, {
            deletedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }));
    }

    public createStickerAsset(record: StickerAsset): StickerAsset {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const asset = this.withSyncMetadata(
            this.normalizeStickerAssetDefaults({
                ...record,
                ownerId: this.isAdminDataUser(actor) ? (record.ownerId || actor.id) : actor.id
            }),
            record.ownerId || actor.id,
            null
        );
        this.assertCanManageStickerAsset(actor, asset);
        if (state.stickerAssets.some((item) => item.id === asset.id)) {
            throw new Error(`Duplicate stickerAssets id: ${asset.id}`);
        }
        state.stickerAssets.push(asset);
        this.writeState(state);
        return asset;
    }

    public updateStickerAsset(id: string, patch: Partial<StickerAsset>): StickerAsset | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.stickerAssets.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeStickerAssetDefaults(state.stickerAssets[index]);
        this.assertCanManageStickerAsset(actor, existing);
        const updated = this.normalizeStickerAssetDefaults({
            ...existing,
            ...patch,
            id: existing.id,
            ownerId: existing.ownerId,
            updatedAt: patch.updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion)
        });
        this.assertCanManageStickerAsset(actor, updated);

        state.stickerAssets[index] = updated;
        this.writeState(state);
        return updated;
    }

    public deleteStickerAsset(id: string): boolean {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.stickerAssets.findIndex((item) => item.id === id);
        if (index < 0) return false;

        this.assertCanManageStickerAsset(actor, this.normalizeStickerAssetDefaults(state.stickerAssets[index]));
        state.stickerAssets[index] = this.softDeleteRecord(state.stickerAssets[index]);
        this.writeState(state);
        return true;
    }

    public createDecoration(record: Decoration): Decoration {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const decoration = this.withSyncMetadata({
            ...record,
            userId: this.isAdminDataUser(actor) ? (record.userId || record.ownerId || actor.id) : actor.id
        }, record.ownerId || record.userId || actor.id, record.groupId || null);
        this.assertCanManageDecoration(actor, decoration);
        if (state.decorations.some((item) => item.id === decoration.id)) {
            throw new Error(`Duplicate decorations id: ${decoration.id}`);
        }
        state.decorations.push(decoration);
        this.writeState(state);
        return decoration;
    }

    public updateDecoration(id: string, patch: Partial<Decoration>): Decoration | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.decorations.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = state.decorations[index];
        this.assertCanManageDecoration(actor, existing);
        const updated = {
            ...existing,
            ...patch,
            id: existing.id,
            userId: this.isAdminDataUser(actor) ? (patch.userId || existing.userId) : existing.userId,
            updatedAt: patch.updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(existing, patch.syncVersion)
        } as Decoration;
        this.assertCanManageDecoration(actor, updated);
        state.decorations[index] = updated;
        this.writeState(state);
        return updated;
    }

    public deleteDecoration(id: string): boolean {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.decorations.findIndex((item) => item.id === id);
        if (index < 0) return false;

        this.assertCanManageDecoration(actor, state.decorations[index]);
        state.decorations[index] = this.softDeleteRecord(state.decorations[index]);
        this.writeState(state);
        return true;
    }

    public createTheme(record: Theme): Theme {
        return this.createRecord('themes', record);
    }

    public updateTheme(id: string, patch: Partial<Theme>): Theme | null {
        return this.updateRecord('themes', id, patch);
    }

    public deleteTheme(id: string): boolean {
        return this.deleteRecord('themes', id);
    }

    private ensureSeeded(user: User): OwnitPersistedState {
        const state = this.readState();
        this.upsertInState(state, 'users', user);

        state.themes = this.withDefaultThemes(state.themes);

        if (this.isDemoModeEnabled() && !state.seededUserIds.includes(user.id)) {
            const seed = this.markSampleSeedData(createSampleData(user));
            this.upsertManyInState(state, 'users', [seed.user]);
            this.upsertManyInState(state, 'calendars', seed.calendars);
            this.upsertManyInState(state, 'groups', seed.groups);
            this.upsertManyInState(state, 'events', seed.events);
            this.upsertManyInState(state, 'todos', seed.todos);
            this.upsertManyInState(state, 'todoCategories', seed.todoCategories);
            this.upsertManyInState(state, 'todoGoals', seed.todoGoals);
            this.upsertManyInState(state, 'todoRoutines', seed.todoRoutines);
            this.upsertManyInState(state, 'todoDailyRecords', seed.todoDailyRecords);
            this.upsertManyInState(state, 'todoCompletionRecords', seed.todoCompletionRecords);
            this.upsertManyInState(state, 'todoPostponeRecords', seed.todoPostponeRecords);
            this.upsertManyInState(state, 'todoTimerSessions', seed.todoTimerSessions);
            this.upsertManyInState(state, 'todoDiaries', seed.todoDiaries);
            this.upsertManyInState(state, 'todoReactions', seed.todoReactions);
            this.upsertManyInState(state, 'stickerAssets', seed.stickerAssets);
            this.upsertManyInState(state, 'decorations', seed.decorations);
            this.upsertManyInState(state, 'themes', seed.themes);
            state.seededUserIds.push(user.id);
        }

        if (!this.isDemoModeEnabled()) {
            this.removeSampleSeedData(state, user.id);
        }

        this.ensureDefaultWorkspace(state, user);

        this.writeState(state);
        return state;
    }

    private markSampleSeedData(seed: ReturnType<typeof createSampleData>): ReturnType<typeof createSampleData> {
        const mark = <T extends { [key: string]: any }>(record: T, fallbackSource: SyncRecordSource = 'seed'): T => ({
            ...record,
            source: record.source || fallbackSource,
            syncVersion: Math.max(1, Number(record.syncVersion) || 1)
        });

        return {
            ...seed,
            user: mark(seed.user, 'user'),
            calendars: seed.calendars.map((calendar) => mark(calendar, calendar.isDefault ? 'system' : 'seed')),
            groups: seed.groups.map((group) => ({
                ...mark(group, 'seed'),
                members: group.members.map((member) => mark(member, 'seed'))
            })),
            events: seed.events.map((event) => mark(event, 'seed')),
            todos: seed.todos.map((todo) => mark(todo, 'seed')),
            todoCategories: seed.todoCategories.map((category) => mark(category, 'seed')),
            todoGoals: seed.todoGoals.map((goal) => mark(goal, 'seed')),
            todoRoutines: seed.todoRoutines.map((routine) => mark(routine, 'seed')),
            todoDailyRecords: seed.todoDailyRecords.map((record) => mark(record, 'seed')),
            todoCompletionRecords: seed.todoCompletionRecords.map((record) => mark(record, 'seed')),
            todoPostponeRecords: seed.todoPostponeRecords.map((record) => mark(record, 'seed')),
            todoTimerSessions: seed.todoTimerSessions.map((session) => mark(session, 'seed')),
            todoDiaries: seed.todoDiaries.map((diary) => mark(diary, 'seed')),
            todoReactions: seed.todoReactions.map((reaction) => mark(reaction, 'seed')),
            stickerAssets: seed.stickerAssets.map((asset) => mark(asset, 'seed')),
            decorations: seed.decorations.map((decoration) => ({
                ...mark(decoration, 'seed'),
                stickers: decoration.stickers.map((sticker) => mark(sticker, 'seed'))
            })),
            themes: seed.themes.map((theme) => mark(theme, 'system'))
        };
    }

    private removeSampleSeedData(state: OwnitPersistedState, userId: string): void {
        const sampleCalendarIds = new Set(this.sampleCalendarSuffixes.map((suffix) => `cal-${userId}-${suffix}`));
        const sampleGroupIds = new Set(this.sampleGroupSuffixes.map((suffix) => `group-${userId}-${suffix}`));
        const sampleEventIds = new Set(this.sampleEventSuffixes.map((suffix) => `event-${userId}-${suffix}`));
        const sampleTodoIds = new Set(this.sampleTodoSuffixes.map((suffix) => `todo-${userId}-${suffix}`));
        const sampleTodoCategoryIds = new Set(this.sampleTodoCategorySuffixes.map((suffix) => `todo-category-${userId}-${suffix}`));
        const sampleTodoGoalIds = new Set(this.sampleTodoGoalSuffixes.map((suffix) => `todo-goal-${userId}-${suffix}`));
        const sampleTodoRoutineIds = new Set(this.sampleTodoRoutineSuffixes.map((suffix) => `todo-routine-${userId}-${suffix}`));
        const sampleStickerIds = new Set(this.sampleStickerSuffixes.map((suffix) => `sticker-${userId}-${suffix}`));

        state.calendars = state.calendars.filter((calendar) => {
            if (!sampleCalendarIds.has(calendar.id)) return true;
            return this.hasNonSampleEventsForCalendar(state, calendar.id, sampleEventIds) && !this.isDevelopmentSeedCalendar(calendar);
        });
        state.groups = state.groups.filter((group) => !sampleGroupIds.has(group.id) || this.hasNonSampleCalendarForGroup(state, group.id, sampleCalendarIds));
        state.events = state.events.filter((event) => !sampleEventIds.has(event.id));
        state.todos = state.todos.filter((todo) => !sampleTodoIds.has(todo.id));
        state.todoCategories = state.todoCategories.filter((category) => !sampleTodoCategoryIds.has(category.id));
        state.todoGoals = state.todoGoals.filter((goal) => !sampleTodoGoalIds.has(goal.id));
        state.todoRoutines = state.todoRoutines.filter((routine) => !sampleTodoRoutineIds.has(routine.id));
        state.todoDailyRecords = state.todoDailyRecords.filter((record) => record.userId !== userId || Boolean(record.diary));
        state.todoCompletionRecords = state.todoCompletionRecords.filter((record) => (!record.todoId || !sampleTodoIds.has(record.todoId)) && (!record.routineId || !sampleTodoRoutineIds.has(record.routineId)));
        state.todoPostponeRecords = state.todoPostponeRecords.filter((record) => (!record.todoId || !sampleTodoIds.has(record.todoId)) && (!record.routineId || !sampleTodoRoutineIds.has(record.routineId)));
        state.todoTimerSessions = state.todoTimerSessions.filter((session) => !sampleTodoIds.has(session.todoId));
        state.todoDiaries = state.todoDiaries.filter((diary) => !diary.todoId || !sampleTodoIds.has(diary.todoId));
        state.todoReactions = state.todoReactions.filter((reaction) => !(reaction.targetType === 'todo' && reaction.targetId && sampleTodoIds.has(reaction.targetId)));
        state.decorations = state.decorations.map((decoration) => {
            if (decoration.userId !== userId) return decoration;
            return {
                ...decoration,
                stickers: decoration.stickers.filter((sticker) => !sampleStickerIds.has(sticker.id))
            };
        });
        state.calendars = state.calendars.filter((calendar) => {
            if (!this.isSampleSeedOwnedRecord(calendar, userId)) return true;
            return this.hasNonSampleEventsForCalendar(state, calendar.id, sampleEventIds) && !this.isDevelopmentSeedCalendar(calendar);
        });
        state.groups = state.groups.filter((group) => {
            if (!this.isSampleSeedOwnedRecord(group, userId)) return true;
            return this.hasNonSampleCalendarForGroup(state, group.id, sampleCalendarIds);
        });
        state.events = state.events.filter((event) => !this.isSampleSeedOwnedRecord(event, userId));
        state.todos = state.todos.filter((todo) => !this.isSampleSeedOwnedRecord(todo, userId));
        state.todoCategories = state.todoCategories.filter((category) => !this.isSampleSeedOwnedRecord(category, userId));
        state.todoGoals = state.todoGoals.filter((goal) => !this.isSampleSeedOwnedRecord(goal, userId));
        state.todoRoutines = state.todoRoutines.filter((routine) => !this.isSampleSeedOwnedRecord(routine, userId));
        state.todoCompletionRecords = state.todoCompletionRecords.filter((record) => !this.isSampleSeedOwnedRecord(record, userId));
        state.todoPostponeRecords = state.todoPostponeRecords.filter((record) => !this.isSampleSeedOwnedRecord(record, userId));
        state.todoTimerSessions = state.todoTimerSessions.filter((session) => !this.isSampleSeedOwnedRecord(session, userId));
        state.todoDiaries = state.todoDiaries.filter((diary) => !this.isSampleSeedOwnedRecord(diary, userId));
        state.todoReactions = state.todoReactions.filter((reaction) => !this.isSampleSeedOwnedRecord(reaction, userId));
        state.stickerAssets = state.stickerAssets.filter((asset) => !this.isSampleSeedOwnedRecord(asset, userId));
        state.decorations = state.decorations
            .filter((decoration) => !this.isSampleSeedOwnedRecord(decoration, userId))
            .map((decoration) => ({
                ...decoration,
                stickers: decoration.stickers.filter((sticker) => !this.isSampleSeedOwnedRecord(sticker, userId))
            }));
        state.todoDailyRecords = this.withDerivedDailyRecords(state, state.todoDailyRecords);
        state.seededUserIds = state.seededUserIds.filter((id) => id !== userId);
    }

    private isDemoModeEnabled(): boolean {
        if (typeof window === 'undefined' || !window.localStorage) return false;
        try {
            const queryEnabled = new URL(window.location.href).searchParams.get('demo') === '1';
            return queryEnabled || window.localStorage.getItem(this.demoModeKey) === 'true';
        } catch (error) {
            return window.localStorage.getItem(this.demoModeKey) === 'true';
        }
    }

    private ensureDefaultWorkspace(state: OwnitPersistedState, user: User): void {
        const normalizedUser = normalizeDataUser(user);
        this.removeLegacyMockWorkspace(state, normalizedUser);

        const defaultCalendars = createDefaultCalendars(normalizedUser);
        for (const calendar of defaultCalendars) {
            const existingIndex = state.calendars.findIndex((item) => item.id === calendar.id);
            if (existingIndex < 0) {
                state.calendars.push(this.normalizeCalendarDefaults(calendar, 0));
                continue;
            }
            const existing = this.normalizeCalendarDefaults(state.calendars[existingIndex], existingIndex);
            state.calendars[existingIndex] = {
                ...existing,
                type: 'personal',
                ownerId: normalizedUser.id,
                groupId: null,
                color: neutralizeLegacyOwnitColor(existing.color || calendar.color),
                description: existing.description || calendar.description,
                isDefault: true,
                archivedAt: null,
                deletedAt: null,
                updatedAt: new Date().toISOString()
            };
        }
    }

    private createSnapshot(state: OwnitPersistedState, userId: string, snapshotUser: User | null = null, includeDeleted = false): OwnitDataSnapshot {
        const id = userId || DEFAULT_GUEST_USER_ID;
        const user = snapshotUser || state.users.find((item) => item.id === id) || normalizeDataUser({
            id,
            username: id,
            email: '',
            authProvider: 'email',
            role: 'owner',
            name: '',
            nickname: '',
            profileImage: '',
            defaultFont: OWNIT_DEFAULT_FONT_ID,
            defaultTheme: OWNIT_DEFAULT_THEME_ID,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
        const isAdmin = this.isAdminDataUser(user);
        const activeGroups = state.groups
            .map((group) => this.normalizeGroupDefaults(group))
            .filter((group) => includeDeleted || this.isActiveGroup(group));
        const activeCalendars = state.calendars
            .map((calendar, index) => this.normalizeCalendarDefaults(calendar, index))
            .filter((calendar) => includeDeleted || this.isActiveCalendar(calendar));
        const groups = activeGroups.filter((group) => {
            if (isAdmin) return true;
            return canViewGroup(user, group);
        });
        const groupById = new Map(groups.map((group) => [group.id, group]));
        const calendars = this.sortCalendars(activeCalendars.filter((calendar) => {
            const group = calendar.type === 'shared' && calendar.groupId ? groupById.get(calendar.groupId) || null : null;
            return canViewCalendar(user, calendar, group);
        }));
        const calendarById = new Map(calendars.map((calendar) => [calendar.id, calendar]));
        const events = state.events.filter((event) => {
            if (!includeDeleted && this.isDeletedRecord(event)) return false;
            const calendar = calendarById.get(event.calendarId) || null;
            const group = calendar?.type === 'shared' && calendar.groupId ? groupById.get(calendar.groupId) || null : null;
            return canViewEvent(user, event, calendar, group);
        });
        const activeTodos = state.todos.filter((todo) => includeDeleted || !this.isDeletedRecord(todo));
        const todos = isAdmin ? [...activeTodos] : activeTodos.filter((todo) => todo.authorId === id || todo.assigneeId === id || todo.createdBy === id || todo.assignedTo === id);
        const todoIds = todos.map((todo) => todo.id);
        const activeTodoCategories = state.todoCategories.filter((category) => includeDeleted || !this.isDeletedRecord(category));
        const activeTodoGoals = state.todoGoals.filter((goal) => includeDeleted || !this.isDeletedRecord(goal));
        const activeTodoRoutines = state.todoRoutines.filter((routine) => includeDeleted || !this.isDeletedRecord(routine));
        const todoCategories = isAdmin ? [...activeTodoCategories] : activeTodoCategories.filter((category) => category.ownerId === id || category.userId === id);
        const todoGoals = isAdmin ? [...activeTodoGoals] : activeTodoGoals.filter((goal) => goal.ownerId === id || goal.userId === id);
        const todoRoutines = isAdmin ? [...activeTodoRoutines] : activeTodoRoutines.filter((routine) => routine.ownerId === id || routine.userId === id || routine.assigneeId === id || routine.assignedTo === id);
        const routineIds = todoRoutines.map((routine) => routine.id);
        const activeDailyRecords = state.todoDailyRecords.filter((record) => includeDeleted || !this.isDeletedRecord(record));
        const activeCompletionRecords = state.todoCompletionRecords.filter((record) => includeDeleted || !this.isDeletedRecord(record));
        const activePostponeRecords = state.todoPostponeRecords.filter((record) => includeDeleted || !this.isDeletedRecord(record));
        const activeTimerSessions = state.todoTimerSessions.filter((session) => includeDeleted || !this.isDeletedRecord(session));
        const activeDiaries = state.todoDiaries.filter((diary) => includeDeleted || !this.isDeletedRecord(diary));
        const activeReactions = state.todoReactions.filter((reaction) => includeDeleted || !this.isDeletedRecord(reaction));
        const activeStickerAssets = state.stickerAssets.filter((asset) => includeDeleted || !this.isDeletedRecord(asset));
        const todoDailyRecords = isAdmin ? [...activeDailyRecords] : activeDailyRecords.filter((record) => record.userId === id);
        const todoCompletionRecords = isAdmin ? [...activeCompletionRecords] : activeCompletionRecords.filter((record) => record.authorId === id || Boolean(record.todoId && todoIds.includes(record.todoId)) || Boolean(record.routineId && routineIds.includes(record.routineId)));
        const todoPostponeRecords = isAdmin ? [...activePostponeRecords] : activePostponeRecords.filter((record) => Boolean(record.todoId && todoIds.includes(record.todoId)) || Boolean(record.routineId && routineIds.includes(record.routineId)));
        const todoTimerSessions = isAdmin ? [...activeTimerSessions] : activeTimerSessions.filter((session) => todoIds.includes(session.todoId));
        const todoDiaries = isAdmin ? [...activeDiaries] : activeDiaries.filter((diary) => diary.authorId === id || Boolean(diary.todoId && todoIds.includes(diary.todoId)) || Boolean(diary.routineId && routineIds.includes(diary.routineId)));
        const todoReactions = isAdmin ? [...activeReactions] : activeReactions.filter((reaction) => reaction.authorId === id || reaction.userId === id || (reaction.targetType === 'todo' && Boolean(reaction.targetId && todoIds.includes(reaction.targetId))) || (reaction.targetType === 'routine' && Boolean(reaction.targetId && routineIds.includes(reaction.targetId))));
        const stickerAssets = isAdmin ? [...activeStickerAssets] : activeStickerAssets.filter((asset) => asset.ownerId === id);
        const decorations = state.decorations.filter((decoration) => decoration.userId === id && (includeDeleted || !this.isDeletedRecord(decoration)));

        return {
            user,
            calendars,
            groups,
            events,
            todos,
            todoCategories,
            todoGoals,
            todoRoutines,
            todoDailyRecords,
            todoCompletionRecords,
            todoPostponeRecords,
            todoTimerSessions,
            todoDiaries,
            todoReactions,
            stickerAssets,
            decorations,
            themes: state.themes.filter((theme) => includeDeleted || !this.isDeletedRecord(theme))
        };
    }

    private countBackupCollections(snapshot: OwnitDataSnapshot): OwnitBackupEnvelope['manifest']['collections'] {
        return {
            user: snapshot.user ? 1 : 0,
            calendars: snapshot.calendars.length,
            groups: snapshot.groups.length,
            events: snapshot.events.length,
            todos: snapshot.todos.length,
            todoCategories: snapshot.todoCategories.length,
            todoGoals: snapshot.todoGoals.length,
            todoRoutines: snapshot.todoRoutines.length,
            todoDailyRecords: snapshot.todoDailyRecords.length,
            todoCompletionRecords: snapshot.todoCompletionRecords.length,
            todoPostponeRecords: snapshot.todoPostponeRecords.length,
            todoTimerSessions: snapshot.todoTimerSessions.length,
            todoDiaries: snapshot.todoDiaries.length,
            todoReactions: snapshot.todoReactions.length,
            stickerAssets: snapshot.stickerAssets.length,
            decorations: snapshot.decorations.length,
            themes: snapshot.themes.length
        };
    }

    private userNamespace(userId: string): string {
        return `user:${userId || DEFAULT_GUEST_USER_ID}`;
    }

    private groupNamespace(groupId: string): string {
        return `group:${groupId}`;
    }

    private mergeRecordsInState<K extends CollectionName>(state: OwnitPersistedState, collection: K, records: CollectionMap[K] | undefined): void {
        if (!Array.isArray(records)) return;
        records.forEach((record) => this.mergeRecordInState(state, collection, record));
    }

    private mergeRecordInState<K extends CollectionName>(state: OwnitPersistedState, collection: K, record: CollectionRecord<K> | undefined): void {
        if (!record?.id) return;
        const items = this.collection(state, collection);
        const index = items.findIndex((item) => item.id === record.id);
        if (index < 0) {
            items.push(record);
            return;
        }
        if (this.shouldReplaceRecord(items[index], record)) {
            items[index] = { ...items[index], ...record } as CollectionRecord<K>;
        }
    }

    protected shouldReplaceRecord(current: { updatedAt?: string; syncVersion?: number }, incoming: { updatedAt?: string; syncVersion?: number }): boolean {
        const currentVersion = Number(current.syncVersion) || 0;
        const incomingVersion = Number(incoming.syncVersion) || 0;
        if (incomingVersion !== currentVersion) return incomingVersion > currentVersion;
        return new Date(incoming.updatedAt || 0).getTime() >= new Date(current.updatedAt || 0).getTime();
    }

    private createRecord<K extends CollectionName>(collection: K, record: CollectionRecord<K>): CollectionRecord<K> {
        const state = this.readState();
        const items = this.collection(state, collection);
        if (items.some((item) => item.id === record.id)) {
            throw new Error(`Duplicate ${collection} id: ${record.id}`);
        }
        const created = this.withSyncMetadata(record as any, this.ownerIdForRecord(record), this.groupIdForRecord(record)) as CollectionRecord<K>;
        items.push(created);
        this.writeState(state);
        return created;
    }

    private updateRecord<K extends CollectionName>(collection: K, id: string, patch: Partial<CollectionRecord<K>>): CollectionRecord<K> | null {
        const state = this.readState();
        const items = this.collection(state, collection);
        const index = items.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const updated = {
            ...items[index],
            ...patch,
            updatedAt: (patch as any).updatedAt || new Date().toISOString(),
            syncVersion: this.nextSyncVersion(items[index], (patch as any).syncVersion)
        } as CollectionRecord<K>;
        items[index] = updated;
        this.writeState(state);
        return updated;
    }

    private deleteRecord<K extends CollectionName>(collection: K, id: string): boolean {
        const state = this.readState();
        const items = this.collection(state, collection);
        const index = items.findIndex((item) => item.id === id);
        if (index < 0) return false;

        items[index] = this.softDeleteRecord(items[index]) as CollectionRecord<K>;
        this.writeState(state);
        return true;
    }

    private softDeleteRecord<T extends { [key: string]: any }>(record: T): T {
        const now = new Date().toISOString();
        return {
            ...record,
            deletedAt: record.deletedAt || now,
            updatedAt: now,
            localUpdatedAt: now,
            syncVersion: this.nextSyncVersion(record)
        };
    }

    private nextSyncVersion(record: { syncVersion?: number | string } | null | undefined, requested?: number | string): number {
        const current = Math.max(1, Number(record?.syncVersion) || 1);
        const explicit = Number(requested);
        if (Number.isFinite(explicit) && explicit > current) return explicit;
        return current + 1;
    }

    private updateCalendarStatus(id: string, patch: Partial<Pick<Calendar, 'archivedAt' | 'deletedAt'>>): Calendar | null {
        const state = this.readState();
        const actor = this.requireActiveUser(state);
        const index = state.calendars.findIndex((item) => item.id === id);
        if (index < 0) return null;

        const existing = this.normalizeCalendarDefaults(state.calendars[index], index);
        if (existing.isDefault) return null;
        this.assertCanManageCalendar(actor, existing, state);

        const now = new Date().toISOString();
        const updated = this.normalizeCalendarDefaults({
            ...existing,
            ...patch,
            syncVersion: this.nextSyncVersion(existing, (patch as Partial<Calendar>).syncVersion),
            localUpdatedAt: now,
            updatedAt: now
        }, existing.order);
        state.calendars[index] = updated;
        this.writeState(state);
        return updated;
    }

    private upsertManyInState<K extends CollectionName>(state: OwnitPersistedState, collection: K, records: Array<CollectionRecord<K>>): void {
        records.forEach((record) => this.upsertInState(state, collection, record));
    }

    private upsertInState<K extends CollectionName>(state: OwnitPersistedState, collection: K, record: CollectionRecord<K>): void {
        const items = this.collection(state, collection);
        const index = items.findIndex((item) => item.id === record.id);
        if (index >= 0) {
            items[index] = { ...items[index], ...record } as CollectionRecord<K>;
            return;
        }
        items.push(record);
    }

    private collection<K extends CollectionName>(state: OwnitPersistedState, collection: K): Array<CollectionRecord<K>> {
        return state[collection] as Array<CollectionRecord<K>>;
    }

    protected readState(): OwnitPersistedState {
        const raw = this.storage.getItem(this.storageKey);
        if (!raw) return this.createEmptyState();

        try {
            const parsed = JSON.parse(raw) as Partial<OwnitPersistedState>;
            return this.migrateState(parsed);
        } catch (error) {
            return this.createEmptyState();
        }
    }

    protected writeState(state: OwnitPersistedState): void {
        const syncedState = this.normalizeStateForSync(state);
        const nextState: OwnitPersistedState = {
            ...syncedState,
            version: OWNIT_DATA_VERSION,
            updatedAt: new Date().toISOString()
        };
        this.storage.setItem(this.storageKey, JSON.stringify(nextState));
    }

    private migrateState(state: Partial<OwnitPersistedState>): OwnitPersistedState {
        const empty = this.createEmptyState();
        const migrated: OwnitPersistedState = {
            version: OWNIT_DATA_VERSION,
            seededUserIds: Array.isArray(state.seededUserIds) ? state.seededUserIds : empty.seededUserIds,
            users: Array.isArray(state.users) ? state.users.map((user) => this.normalizeUserDefaults(user as User)) : empty.users,
            calendars: Array.isArray(state.calendars) ? state.calendars.map((calendar, index) => this.normalizeCalendarDefaults(calendar as Partial<Calendar>, index)) : empty.calendars,
            groups: Array.isArray(state.groups) ? state.groups.map((group) => this.normalizeGroupDefaults(group as Partial<Group>)) : empty.groups,
            events: Array.isArray(state.events) ? state.events.map((event) => ({
                ...event,
                color: neutralizeLegacyOwnitColor(event.color)
            })) : empty.events,
            todos: Array.isArray(state.todos) ? state.todos.map((todo, index) => this.normalizeTodoDefaults(todo as Partial<Todo>, index)) : empty.todos,
            todoCategories: Array.isArray(state.todoCategories) ? state.todoCategories.map((category, index) => this.normalizeTodoCategoryDefaults(category as Partial<TodoCategory>, index)) : empty.todoCategories,
            todoGoals: Array.isArray(state.todoGoals) ? state.todoGoals.map((goal, index) => this.normalizeTodoGoalDefaults(goal as Partial<TodoGoal>, index)) : empty.todoGoals,
            todoRoutines: Array.isArray(state.todoRoutines) ? state.todoRoutines.map((routine) => this.normalizeTodoRoutineDefaults(routine as Partial<TodoRoutine>)) : empty.todoRoutines,
            todoDailyRecords: Array.isArray(state.todoDailyRecords) ? state.todoDailyRecords.map((record) => this.normalizeTodoDailyRecordDefaults(record as Partial<TodoDailyRecord>)) : empty.todoDailyRecords,
            todoCompletionRecords: Array.isArray(state.todoCompletionRecords) ? state.todoCompletionRecords.map((record) => this.normalizeTodoCompletionRecordDefaults(record as Partial<TodoCompletionRecord>)) : empty.todoCompletionRecords,
            todoPostponeRecords: Array.isArray(state.todoPostponeRecords) ? state.todoPostponeRecords.map((record) => this.normalizeTodoPostponeRecordDefaults(record as Partial<TodoPostponeRecord>)) : empty.todoPostponeRecords,
            todoTimerSessions: Array.isArray(state.todoTimerSessions) ? state.todoTimerSessions.map((session) => this.normalizeTodoTimerSessionDefaults(session as Partial<TodoTimerSession>)) : empty.todoTimerSessions,
            todoDiaries: Array.isArray(state.todoDiaries) ? state.todoDiaries.map((diary) => ({
                ...diary,
                todoId: diary.todoId || null,
                routineId: diary.routineId || null,
                reactionId: diary.reactionId || null
            })) : empty.todoDiaries,
            todoReactions: Array.isArray(state.todoReactions) ? state.todoReactions.map((reaction) => this.normalizeTodoReactionDefaults(reaction as Partial<TodoReaction>)) : empty.todoReactions,
            stickerAssets: Array.isArray(state.stickerAssets) ? state.stickerAssets.map((asset) => this.normalizeStickerAssetDefaults(asset as Partial<StickerAsset>)) : empty.stickerAssets,
            decorations: Array.isArray(state.decorations) ? state.decorations.map((decoration) => ({
                ...decoration,
                fontId: normalizeOwnitFontId(decoration.fontId)
            })) : empty.decorations,
            themes: this.withDefaultThemes(Array.isArray(state.themes) ? state.themes : empty.themes),
            updatedAt: state.updatedAt || empty.updatedAt
        };
        this.withTodoCategoryFallbacks(migrated);
        migrated.todos = this.withTodoTimerSessionIds(migrated.todos, migrated.todoTimerSessions);
        migrated.todoDailyRecords = this.withDerivedDailyRecords(migrated, migrated.todoDailyRecords);
        return this.normalizeStateForSync(migrated);
    }

    private withTodoCategoryFallbacks(state: OwnitPersistedState): void {
        const activeCategories = state.todoCategories.filter((category) => !this.isDeletedRecord(category));
        const categoryById = new Map(activeCategories.map((category) => [category.id, category]));
        const categoryByName = new Map(activeCategories.map((category) => [this.categoryLookupKey(category.name), category]));

        const ensureCategoryForGoal = (goal: TodoGoal, fallbackOrder: number): TodoCategory => {
            const linked = goal.categoryId ? categoryById.get(goal.categoryId) || null : null;
            if (linked) return linked;

            const named = categoryByName.get(this.categoryLookupKey(goal.name)) || null;
            if (named) return named;

            const now = goal.createdAt || goal.updatedAt || new Date().toISOString();
            let id = goal.id.replace(/^todo-goal/, 'todo-category');
            if (!id || id === goal.id || categoryById.has(id)) id = `todo-category-${goal.ownerId || goal.userId || DEFAULT_GUEST_USER_ID}-${fallbackOrder}`;
            while (categoryById.has(id)) id = `${id}-${categoryById.size}`;

            const created = this.normalizeTodoCategoryDefaults({
                id,
                userId: goal.userId,
                ownerId: goal.ownerId,
                groupId: goal.groupId || null,
                name: goal.name,
                color: goal.color,
                visibility: goal.visibility,
                order: goal.order,
                sortOrder: goal.order,
                collapsed: goal.collapsed,
                locked: goal.locked,
                archived: goal.status === 'archived',
                archivedAt: goal.status === 'archived' ? now : null,
                createdAt: now,
                updatedAt: goal.updatedAt || now,
                source: goal.source || 'legacy'
            }, fallbackOrder);
            state.todoCategories.push(created);
            categoryById.set(created.id, created);
            categoryByName.set(this.categoryLookupKey(created.name), created);
            return created;
        };

        state.todoGoals = state.todoGoals.map((goal, index) => {
            if (this.isDeletedRecord(goal)) return this.normalizeTodoGoalDefaults(goal, index);
            const category = ensureCategoryForGoal(goal, index);
            return this.normalizeTodoGoalDefaults({
                ...goal,
                categoryId: category.id,
                name: goal.name || category.name,
                color: goal.color || category.color,
                visibility: goal.visibility || category.visibility,
                order: Number.isFinite(Number(goal.order)) ? Number(goal.order) : category.order,
                collapsed: Boolean(goal.collapsed || category.collapsed),
                status: goal.status || (category.archived ? 'archived' : 'active')
            }, index);
        });

        const linkedCategories = state.todoCategories.filter((category) => !this.isDeletedRecord(category));
        const linkedGoals = state.todoGoals.filter((goal) => !this.isDeletedRecord(goal));
        const goalById = new Map(linkedGoals.map((goal) => [goal.id, goal]));
        const categoriesById = new Map(linkedCategories.map((category) => [category.id, category]));
        const categoriesByName = new Map(linkedCategories.map((category) => [this.categoryLookupKey(category.name), category]));
        const goalByCategoryId = new Map(linkedGoals.filter((goal) => goal.categoryId).map((goal) => [goal.categoryId as string, goal]));

        state.todos = state.todos.map((todo, index) => {
            const goal = todo.goalId ? goalById.get(todo.goalId) || null : null;
            const category = (todo.categoryId ? categoriesById.get(todo.categoryId) || null : null)
                || (goal?.categoryId ? categoriesById.get(goal.categoryId) || null : null)
                || (todo.category ? categoriesByName.get(this.categoryLookupKey(todo.category)) || null : null);
            const linkedGoal = goal || (category?.id ? goalByCategoryId.get(category.id) || null : null);
            return this.normalizeTodoDefaults({
                ...todo,
                goalId: linkedGoal?.id || todo.goalId || null,
                categoryId: category?.id || linkedGoal?.categoryId || todo.categoryId || null,
                category: linkedGoal?.name || category?.name || todo.category || '미분류',
                color: linkedGoal?.color || category?.color || todo.color || '#6B6259'
            }, index);
        });

        state.todoRoutines = state.todoRoutines.map((routine) => {
            const goal = routine.goalId ? goalById.get(routine.goalId) || null : null;
            const category = (routine.categoryId ? categoriesById.get(routine.categoryId) || null : null)
                || (goal?.categoryId ? categoriesById.get(goal.categoryId) || null : null)
                || (routine.memo ? categoriesByName.get(this.categoryLookupKey(routine.memo)) || null : null);
            const linkedGoal = goal || (category?.id ? goalByCategoryId.get(category.id) || null : null);
            return this.normalizeTodoRoutineDefaults({
                ...routine,
                goalId: linkedGoal?.id || routine.goalId || null,
                categoryId: category?.id || linkedGoal?.categoryId || routine.categoryId || null,
                memo: linkedGoal?.name || category?.name || routine.memo || '루틴',
                color: linkedGoal?.color || category?.color || routine.color || '#B59A5C'
            });
        });
    }

    private categoryLookupKey(value: string | null | undefined): string {
        return (value || '').trim().toLowerCase();
    }

    private createEmptyState(): OwnitPersistedState {
        return this.normalizeStateForSync({
            version: OWNIT_DATA_VERSION,
            seededUserIds: [],
            users: [],
            calendars: [],
            groups: [],
            events: [],
            todos: [],
            todoCategories: [],
            todoGoals: [],
            todoRoutines: [],
            todoDailyRecords: [],
            todoCompletionRecords: [],
            todoPostponeRecords: [],
            todoTimerSessions: [],
            todoDiaries: [],
            todoReactions: [],
            stickerAssets: [],
            decorations: [],
            themes: createDefaultThemes(),
            updatedAt: new Date().toISOString()
        });
    }

    private normalizeStateForSync(state: OwnitPersistedState): OwnitPersistedState {
        const calendarById = new Map(state.calendars.map((calendar) => [calendar.id, calendar]));

        return {
            ...state,
            users: state.users.map((user) => this.withSyncMetadata(user, user.id || DEFAULT_GUEST_USER_ID, null)),
            calendars: state.calendars.map((calendar) => this.withSyncMetadata(calendar, calendar.ownerId || DEFAULT_GUEST_USER_ID, calendar.groupId || null)),
            groups: state.groups.map((group) => {
                const syncedGroup = this.withSyncMetadata(group, group.adminId || DEFAULT_GUEST_USER_ID, group.id || null);
                return {
                    ...syncedGroup,
                    members: (syncedGroup.members || []).map((member) => this.withSyncMetadata(member, member.userId || syncedGroup.adminId || DEFAULT_GUEST_USER_ID, member.groupId || syncedGroup.id || null))
                };
            }),
            events: state.events.map((event) => {
                const calendar = calendarById.get(event.calendarId);
                return this.withSyncMetadata(event, event.ownerId || event.authorId || DEFAULT_GUEST_USER_ID, event.groupId ?? calendar?.groupId ?? null);
            }),
            todos: state.todos.map((todo) => this.withSyncMetadata(todo, todo.ownerId || todo.createdBy || todo.authorId || DEFAULT_GUEST_USER_ID, todo.groupId || null)),
            todoCategories: state.todoCategories.map((category) => this.withSyncMetadata(category, category.ownerId || category.userId || DEFAULT_GUEST_USER_ID, category.groupId || null)),
            todoGoals: state.todoGoals.map((goal) => this.withSyncMetadata(goal, goal.ownerId || goal.userId || DEFAULT_GUEST_USER_ID, goal.groupId || null)),
            todoRoutines: state.todoRoutines.map((routine) => this.withSyncMetadata(routine, routine.ownerId || routine.userId || DEFAULT_GUEST_USER_ID, routine.groupId || null)),
            todoDailyRecords: state.todoDailyRecords.map((record) => this.withSyncMetadata(record, record.ownerId || record.userId || DEFAULT_GUEST_USER_ID, record.groupId || null)),
            todoCompletionRecords: state.todoCompletionRecords.map((record) => this.withSyncMetadata(record, record.ownerId || record.authorId || DEFAULT_GUEST_USER_ID, record.groupId || null)),
            todoPostponeRecords: state.todoPostponeRecords.map((record) => this.withSyncMetadata(record, record.ownerId || DEFAULT_GUEST_USER_ID, record.groupId || null)),
            todoTimerSessions: state.todoTimerSessions.map((session) => this.withSyncMetadata(session, session.ownerId || DEFAULT_GUEST_USER_ID, session.groupId || null)),
            todoDiaries: state.todoDiaries.map((diary) => this.withSyncMetadata(diary, diary.ownerId || diary.authorId || DEFAULT_GUEST_USER_ID, diary.groupId || null)),
            todoReactions: state.todoReactions.map((reaction) => this.withSyncMetadata(reaction, reaction.ownerId || reaction.authorId || reaction.userId || DEFAULT_GUEST_USER_ID, reaction.groupId || null)),
            stickerAssets: state.stickerAssets.map((asset) => this.withSyncMetadata(this.normalizeStickerAssetDefaults(asset), asset.ownerId || DEFAULT_GUEST_USER_ID, asset.groupId || null)),
            decorations: state.decorations.map((decoration) => ({
                ...this.withSyncMetadata(decoration, decoration.ownerId || decoration.userId || DEFAULT_GUEST_USER_ID, decoration.groupId || null),
                stickers: (decoration.stickers || []).map((sticker) => this.withSyncMetadata(sticker, sticker.ownerId || decoration.userId || DEFAULT_GUEST_USER_ID, sticker.groupId || decoration.groupId || null))
            })),
            themes: state.themes.map((theme) => this.withSyncMetadata(theme, theme.ownerId || 'system', theme.groupId || null, theme.source || 'system'))
        };
    }

    private withSyncMetadata<T extends { [key: string]: any }>(record: T, ownerId: string, groupId: string | null, fallbackSource: SyncRecordSource = 'user'): T {
        const createdAt = record.createdAt || record.updatedAt || new Date().toISOString();
        const syncVersion = Math.max(1, Number(record.syncVersion) || 1);
        return {
            ...record,
            ownerId: record.ownerId || ownerId || DEFAULT_GUEST_USER_ID,
            groupId: record.groupId ?? groupId ?? null,
            deletedAt: record.deletedAt || null,
            syncVersion,
            source: this.normalizeRecordSource(record.source, fallbackSource),
            createdAt,
            updatedAt: record.updatedAt || createdAt
        };
    }

    private ownerIdForRecord(record: { [key: string]: any }): string {
        return record.ownerId
            || record.userId
            || record.authorId
            || record.createdBy
            || record.adminId
            || record.id
            || DEFAULT_GUEST_USER_ID;
    }

    private groupIdForRecord(record: { [key: string]: any }): string | null {
        return record.groupId || null;
    }

    private normalizeRecordSource(source: SyncRecordSource | string | null | undefined, fallback: SyncRecordSource): SyncRecordSource {
        if (source === 'seed' || source === 'system' || source === 'legacy' || source === 'user' || source === 'google' || source === 'microsoft' || source === 'apple' || source === 'naver' || source === 'ics_url' || source === 'mock') return source;
        return fallback;
    }

    private normalizeExternalProvider(provider: Calendar['provider'] | SyncRecordSource | string | null | undefined, fallback: Calendar['provider'] = null): Calendar['provider'] {
        if (provider === 'google' || provider === 'microsoft' || provider === 'apple' || provider === 'naver' || provider === 'ics_url') return provider;
        return fallback || null;
    }

    private withDefaultThemes(themes: Theme[]): Theme[] {
        const byId = new Map<string, Theme>();
        for (const theme of themes) byId.set(theme.id, theme);
        for (const theme of createDefaultThemes()) {
            byId.set(theme.id, { ...byId.get(theme.id), ...theme });
        }
        return Array.from(byId.values());
    }

    private normalizeCalendarDefaults(calendar: Partial<Calendar>, fallbackOrder = 0): Calendar {
        const now = new Date().toISOString();
        const legacyVisibilityValue = (calendar as { visibility?: unknown }).visibility;
        const legacyVisibility = typeof legacyVisibilityValue === 'string' ? legacyVisibilityValue : undefined;
        const ownerId = calendar.ownerId || DEFAULT_GUEST_USER_ID;
        const type = calendar.type === 'shared' ? 'shared' : 'personal';
        const id = calendar.id || `cal-${ownerId}-${type}-${Date.now().toString(36)}`;
        const isDefault = Boolean(calendar.isDefault) || (type === 'personal' && id === `cal-${ownerId}-personal`);
        const createdAt = calendar.createdAt || calendar.updatedAt || now;
        const externalId = calendar.externalId || null;
        const source = this.normalizeRecordSource(calendar.source, externalId ? 'mock' : 'user');
        const provider = this.normalizeExternalProvider(calendar.provider, this.normalizeExternalProvider(source, externalId ? 'ics_url' : null));
        const description = (calendar.description || '').trim()
            || (isDefault ? '회원가입 시 자동 생성되는 개인 기본 캘린더입니다.' : '');

        return {
            id,
            name: (calendar.name || (isDefault ? '기본 개인 캘린더' : '캘린더')).trim(),
            type,
            ownerId,
            groupId: type === 'shared' ? (calendar.groupId || null) : null,
            color: neutralizeLegacyOwnitColor(calendar.color || (type === 'shared' ? '#8A7880' : '#6B6259')),
            description,
            visibility: this.normalizeVisibility(legacyVisibility, type),
            isDefault,
            isVisible: calendar.isVisible !== false && legacyVisibility !== 'hidden',
            order: Number.isFinite(Number(calendar.order)) ? Number(calendar.order) : fallbackOrder,
            archivedAt: isDefault ? null : (calendar.archivedAt || null),
            deletedAt: isDefault ? null : (calendar.deletedAt || null),
            source,
            externalId,
            provider,
            readOnly: Boolean(calendar.readOnly || externalId || provider),
            lastSyncedAt: calendar.lastSyncedAt || null,
            externalUpdatedAt: calendar.externalUpdatedAt || calendar.lastSyncedAt || null,
            localUpdatedAt: calendar.localUpdatedAt || calendar.updatedAt || createdAt,
            syncDirection: this.normalizeCalendarSyncDirection(calendar.syncDirection),
            syncDeletionPolicy: this.normalizeCalendarDeletionPolicy(calendar.syncDeletionPolicy),
            syncStatus: this.normalizeCalendarSyncStatus(calendar.syncStatus, externalId ? 'synced' : 'idle'),
            syncError: calendar.syncError || null,
            externalWritable: calendar.externalWritable === true,
            externalWritePermission: this.normalizeExternalWritePermission(calendar.externalWritePermission),
            externalWriteScope: calendar.externalWriteScope || '',
            createdAt,
            updatedAt: calendar.updatedAt || createdAt
        };
    }

    private normalizeCalendarSyncDirection(direction: string | null | undefined): Calendar['syncDirection'] {
        if (direction === 'export_only' || direction === 'bidirectional') return direction;
        return 'import_only';
    }

    private normalizeCalendarDeletionPolicy(policy: string | null | undefined): Calendar['syncDeletionPolicy'] {
        if (policy === 'mirror_after_confirm') return policy;
        return 'mark_deleted_only';
    }

    private normalizeCalendarSyncStatus(status: string | null | undefined, fallback: NonNullable<Calendar['syncStatus']>): Calendar['syncStatus'] {
        if (status === 'synced' || status === 'pending_import' || status === 'pending_export' || status === 'write_scope_required' || status === 'conflict' || status === 'error' || status === 'deleted_external' || status === 'deleted_local') return status;
        return fallback;
    }

    private normalizeExternalWritePermission(permission: string | null | undefined): Calendar['externalWritePermission'] {
        if (permission === 'requested' || permission === 'granted' || permission === 'denied') return permission;
        return 'not_requested';
    }

    private normalizeGroupDefaults(group: Partial<Group>, fallbackUserId = DEFAULT_GUEST_USER_ID): Group {
        const now = new Date().toISOString();
        const id = (group.id || `group-${fallbackUserId}-${Date.now().toString(36)}`).trim();
        const adminId = group.adminId || fallbackUserId;
        const createdAt = group.createdAt || group.updatedAt || now;
        const members = Array.isArray(group.members) ? group.members : [];
        const normalizedMembers = members.map((member, index) => this.normalizeGroupMemberDefaults(member, id, adminId, createdAt, index));

        if (normalizedMembers.length === 0 && adminId) {
            normalizedMembers.push(this.normalizeGroupMemberDefaults({
                id: `member-${adminId}-owner`,
                groupId: id,
                userId: adminId,
                name: '나',
                nickname: '나',
                email: '',
                role: 'owner',
                status: 'active',
                joinedAt: createdAt
            }, id, adminId, createdAt, 0));
        }

        return {
            id,
            name: (group.name || '공유 그룹').trim(),
            adminId,
            inviteCode: (group.inviteCode || '').trim(),
            inviteLink: (group.inviteLink || '').trim(),
            members: normalizedMembers,
            archivedAt: group.archivedAt || null,
            deletedAt: group.deletedAt || null,
            createdAt,
            updatedAt: group.updatedAt || createdAt
        };
    }

    private normalizeGroupMemberDefaults(member: Partial<GroupMember>, groupId: string, fallbackUserId: string, joinedAt: string, index = 0): GroupMember {
        const userId = member.userId || fallbackUserId;
        const role = member.role === 'admin' || member.role === 'member' || member.role === 'viewer' ? member.role : 'owner';
        const status = this.normalizeGroupMemberStatus(member.status);
        const name = (member.name || member.nickname || (userId === fallbackUserId ? '나' : userId) || '멤버').trim();
        return {
            id: member.id || `member-${groupId}-${userId || index}`,
            groupId,
            userId,
            name,
            nickname: (member.nickname || name).trim(),
            email: (member.email || '').trim(),
            role,
            status,
            joinedAt: member.joinedAt || joinedAt
        };
    }

    private normalizeGroupMemberStatus(status: GroupMemberStatus | null | undefined): GroupMemberStatus {
        if (status === 'invited' || status === 'left' || status === 'removed') return status;
        return 'active';
    }

    private normalizeVisibility(value: string | null | undefined, type: Calendar['type']): Calendar['visibility'] {
        if (value === 'private' || value === 'group' || value === 'public') return value;
        return type === 'shared' ? 'group' : 'private';
    }

    private sortCalendars(calendars: Calendar[]): Calendar[] {
        return [...calendars].sort((a, b) => {
            if (a.type !== b.type) return a.type === 'personal' ? -1 : 1;
            if (a.order !== b.order) return a.order - b.order;
            return a.name.localeCompare(b.name);
        });
    }

    private isActiveCalendar(calendar: Calendar): boolean {
        return !calendar.archivedAt && !calendar.deletedAt;
    }

    private isActiveGroup(group: Group): boolean {
        return !group.archivedAt && !group.deletedAt;
    }

    private isDeletedRecord(record: { deletedAt?: string | null } | null | undefined): boolean {
        return Boolean(record?.deletedAt);
    }

    private isVisibleMemberStatus(status: GroupMemberStatus | null | undefined): boolean {
        return status === 'active' || status === 'invited' || !status;
    }

    private nextCalendarOrder(calendars: Calendar[], ownerId: string, type: Calendar['type']): number {
        const base = type === 'shared' ? 100 : 0;
        const orders = calendars
            .map((calendar, index) => this.normalizeCalendarDefaults(calendar, index))
            .filter((calendar) => calendar.ownerId === ownerId && calendar.type === type)
            .map((calendar) => Number(calendar.order))
            .filter((order) => Number.isFinite(order));
        if (orders.length === 0) return base;
        return Math.max(...orders) + 1;
    }

    private removeLegacyMockWorkspace(state: OwnitPersistedState, user: User): void {
        const legacyCalendarId = `cal-${user.id}-center`;
        const legacyGroupId = `group-${user.id}-center`;
        const sampleEventIds = new Set(this.sampleEventSuffixes.map((suffix) => `event-${user.id}-${suffix}`));
        const calendar = state.calendars.find((item) => item.id === legacyCalendarId);
        const group = state.groups.find((item) => item.id === legacyGroupId);
        const hasUserEvents = this.hasNonSampleEventsForCalendar(state, legacyCalendarId, sampleEventIds);

        if (calendar && this.isLegacyMockCalendar(calendar) && !hasUserEvents) {
            state.calendars = state.calendars.filter((item) => item.id !== legacyCalendarId);
        } else if (calendar && this.isLegacyMockCalendar(calendar) && hasUserEvents) {
            const index = state.calendars.findIndex((item) => item.id === legacyCalendarId);
            state.calendars[index] = this.normalizeCalendarDefaults({
                ...calendar,
                name: '기존 공유 캘린더',
                type: 'shared',
                ownerId: user.id,
                groupId: legacyGroupId,
                description: '이전 버전 공유 일정이 연결된 캘린더입니다.',
                isDefault: false,
                isVisible: true,
                archivedAt: null,
                deletedAt: null,
                updatedAt: new Date().toISOString()
            }, 100);
        }

        if (group && this.isLegacyMockGroup(group) && !hasUserEvents) {
            state.groups = state.groups.filter((item) => item.id !== legacyGroupId);
        } else if (group && this.isLegacyMockGroup(group) && hasUserEvents) {
            const index = state.groups.findIndex((item) => item.id === legacyGroupId);
            const now = new Date().toISOString();
            state.groups[index] = {
                ...group,
                name: '기존 공유 그룹',
                adminId: user.id,
                inviteCode: '',
                inviteLink: '',
                members: [{
                    id: `member-${user.id}-owner`,
                    groupId: legacyGroupId,
                    userId: user.id,
                    name: user.nickname || user.name || '나',
                    nickname: user.nickname || user.name || '나',
                    email: user.email || '',
                    role: user.role === 'admin' ? 'admin' : 'owner',
                    status: 'active',
                    joinedAt: group.members.find((member) => member.userId === user.id)?.joinedAt || now
                }],
                updatedAt: now
            };
        }
    }

    private hasNonSampleEventsForCalendar(state: OwnitPersistedState, calendarId: string, sampleEventIds: Set<string>): boolean {
        if (!calendarId) return false;
        return state.events.some((event) => event.calendarId === calendarId && !sampleEventIds.has(event.id));
    }

    private hasNonSampleCalendarForGroup(state: OwnitPersistedState, groupId: string, sampleCalendarIds: Set<string>): boolean {
        if (!groupId) return false;
        return state.calendars.some((calendar) => calendar.groupId === groupId && !sampleCalendarIds.has(calendar.id) && !calendar.deletedAt);
    }

    private isLegacyMockCalendar(calendar: Partial<Calendar>): boolean {
        return calendar.name === '센터 공유 캘린더' || calendar.description === 'demo seed shared calendar';
    }

    private isDevelopmentSeedCalendar(calendar: Partial<Calendar>): boolean {
        return (calendar.name || '').includes('[개발용]');
    }

    private isSampleSeedOwnedRecord(record: { [key: string]: any } | null | undefined, userId: string): boolean {
        if (!record || record['source'] !== 'seed') return false;
        return record['ownerId'] === userId
            || record['userId'] === userId
            || record['authorId'] === userId
            || record['createdBy'] === userId
            || record['adminId'] === userId;
    }

    private isLegacyMockGroup(group: Group): boolean {
        return group.name === '오운잇 센터'
            || group.inviteCode === 'OWNIT-2026'
            || group.members.some((member) => ['user-min', 'user-han', 'user-seo'].includes(member.userId));
    }

    private normalizeUserDefaults(user: User): User {
        const normalized = normalizeDataUser(user);
        return {
            ...normalized,
            defaultFont: normalizeOwnitFontId(normalized.defaultFont)
        };
    }

    private normalizeStickerAssetDefaults(asset: Partial<StickerAsset>): StickerAsset {
        const now = new Date().toISOString();
        const ownerId = asset.ownerId || DEFAULT_GUEST_USER_ID;
        const createdAt = asset.createdAt || asset.updatedAt || now;
        const dataUrl = (asset.dataUrl || '').trim();
        const originalDataUrl = (asset.originalDataUrl || dataUrl).trim();
        const processedDataUrl = (asset.processedDataUrl || '').trim();
        const name = (asset.name || '내 스티커').trim().slice(0, 40) || '내 스티커';
        const sourceType: StickerAsset['sourceType'] = asset.sourceType === 'basic' ? 'basic' : 'upload';
        const backgroundRemovalStatus: StickerAsset['backgroundRemovalStatus'] =
            asset.backgroundRemovalStatus === 'processing'
                ? 'failed'
                : (asset.backgroundRemovalStatus === 'done' || asset.backgroundRemovalStatus === 'failed' ? asset.backgroundRemovalStatus : 'idle');

        return {
            ...asset,
            id: asset.id || `sticker-asset-${ownerId}-${Date.now().toString(36)}`,
            ownerId,
            imageUrl: asset.imageUrl || null,
            dataUrl,
            originalDataUrl,
            processedDataUrl,
            name,
            sourceType,
            fileType: asset.fileType || this.inferDataUrlType(dataUrl),
            fileSize: Math.max(0, Math.round(Number(asset.fileSize) || 0)),
            originalFileSize: Math.max(0, Math.round(Number(asset.originalFileSize) || Number(asset.fileSize) || 0)),
            width: Math.max(0, Math.round(Number(asset.width) || 0)),
            height: Math.max(0, Math.round(Number(asset.height) || 0)),
            backgroundRemovalStatus,
            backgroundRemovalError: asset.backgroundRemovalError || null,
            renderOptions: {
                border: Boolean(asset.renderOptions?.border),
                shadow: Boolean(asset.renderOptions?.shadow)
            },
            createdAt,
            updatedAt: asset.updatedAt || createdAt
        };
    }

    private inferDataUrlType(value: string): string {
        const match = /^data:([^;]+);/i.exec(value || '');
        return match?.[1] || '';
    }

    private requireActiveUser(state: OwnitPersistedState): User {
        if (!this.activeUser?.id) {
            throw new Error('로그인이 필요합니다.');
        }

        const stored = state.users.find((user) => user.id === this.activeUser?.id);
        const active = normalizeDataUser(stored || this.activeUser);
        this.activeUser = active;
        return active;
    }

    private calendarInState(state: OwnitPersistedState, calendarId: string | null | undefined): Calendar | null {
        if (!calendarId) return null;
        const index = state.calendars.findIndex((calendar) => calendar.id === calendarId);
        if (index < 0) return null;
        const calendar = this.normalizeCalendarDefaults(state.calendars[index], index);
        return this.isActiveCalendar(calendar) ? calendar : null;
    }

    private groupInState(state: OwnitPersistedState, groupId: string | null | undefined): Group | null {
        if (!groupId) return null;
        const group = state.groups
            .map((item) => this.normalizeGroupDefaults(item))
            .find((item) => item.id === groupId) || null;
        return group && this.isActiveGroup(group) ? group : null;
    }

    private groupForCalendarInState(state: OwnitPersistedState, calendar: Calendar | null | undefined): Group | null {
        if (!calendar || calendar.type !== 'shared') return null;
        return this.groupInState(state, calendar.groupId);
    }

    private assertCanCreateCalendar(user: User, calendar: Calendar, state: OwnitPersistedState): void {
        if (this.isAdminDataUser(user)) return;
        if (calendar.type === 'personal') {
            if (calendar.ownerId !== user.id) throw new Error('본인 개인 캘린더만 만들 수 있습니다.');
            return;
        }

        const group = this.groupForCalendarInState(state, calendar);
        if (!group || !hasPermission(user, 'manage_shared_calendar', { group, calendar })) {
            throw new Error('공유 캘린더를 만들 권한이 없습니다.');
        }
    }

    private assertCanManageCalendar(user: User, calendar: Calendar, state: OwnitPersistedState): void {
        if (this.isAdminDataUser(user)) return;
        if (calendar.type === 'personal') {
            if (calendar.ownerId !== user.id) throw new Error('본인 개인 캘린더만 수정할 수 있습니다.');
            return;
        }

        const group = this.groupForCalendarInState(state, calendar);
        if (!group || !hasPermission(user, 'manage_shared_calendar', { group, calendar })) {
            throw new Error('공유 캘린더를 관리할 권한이 없습니다.');
        }
    }

    private assertCanManageEvent(user: User, event: Event | null, calendar: Calendar | null, state: OwnitPersistedState): void {
        if (!calendar) throw new Error('일정의 캘린더를 찾을 수 없습니다.');
        if (calendar.readOnly || calendar.externalId || event?.readOnly || event?.externalId) {
            throw new Error('외부 캘린더 일정은 읽기 전용입니다.');
        }
        const group = this.groupForCalendarInState(state, calendar);
        if (!canManageEvent(user, event, calendar, { group })) {
            throw new Error('일정을 변경할 권한이 없습니다.');
        }
    }

    private assertCanMoveEventBetweenCalendars(user: User, sourceCalendar: Calendar | null, targetCalendar: Calendar | null, state: OwnitPersistedState): void {
        if (!sourceCalendar || !targetCalendar || sourceCalendar.id === targetCalendar.id || this.isAdminDataUser(user)) return;
        if (sourceCalendar.type !== 'shared' && targetCalendar.type !== 'shared') return;

        const canManageSource = sourceCalendar.type !== 'shared'
            || this.canManageSharedCalendarInState(user, sourceCalendar, state);
        const canManageTarget = targetCalendar.type !== 'shared'
            || this.canManageSharedCalendarInState(user, targetCalendar, state);
        if (!canManageSource || !canManageTarget) {
            throw new Error('공유 일정의 캘린더를 변경할 권한이 없습니다.');
        }
    }

    private canManageSharedCalendarInState(user: User, calendar: Calendar, state: OwnitPersistedState): boolean {
        const group = this.groupForCalendarInState(state, calendar);
        return Boolean(group && hasPermission(user, 'manage_shared_calendar', { group, calendar }));
    }

    private assertEventCalendarConsistency(user: User, event: Event, calendar: Calendar, state: OwnitPersistedState): void {
        if (calendar.type === 'personal') {
            if (event.visibility !== 'private') {
                throw new Error('개인 일정은 비공개로만 저장할 수 있습니다.');
            }
            if (!this.isAdminDataUser(user) && event.assigneeId && event.assigneeId !== user.id) {
                throw new Error('개인 일정 담당자는 본인만 지정할 수 있습니다.');
            }
            return;
        }

        const group = this.groupForCalendarInState(state, calendar);
        if (!group) throw new Error('공유 일정의 센터/그룹을 찾을 수 없습니다.');
        if (event.visibility !== 'group') {
            throw new Error('공유 일정은 그룹 공개 범위로만 저장할 수 있습니다.');
        }
        if (event.assigneeId && !this.isActiveGroupMember(group, event.assigneeId)) {
            throw new Error('공유 일정 담당자는 해당 그룹의 참여 구성원만 지정할 수 있습니다.');
        }
        if (!this.isAdminDataUser(user) && event.authorId && !this.isActiveGroupMember(group, event.authorId)) {
            throw new Error('공유 일정 작성자는 해당 그룹의 참여 구성원이어야 합니다.');
        }
    }

    private isActiveGroupMember(group: Group, userId: string): boolean {
        return Boolean(userId && group.members.some((member) => member.userId === userId && member.status === 'active'));
    }

    private assertCanManageTodo(user: User, todo: Todo): void {
        if (this.isAdminDataUser(user)) return;
        if (!this.isOwnTodoRecord(user.id, todo)) {
            throw new Error('본인 Todo만 변경할 수 있습니다.');
        }
    }

    private assertCanManageTodoRoutine(user: User, routine: TodoRoutine): void {
        if (this.isAdminDataUser(user)) return;
        if (!this.isOwnRoutineRecord(user.id, routine)) {
            throw new Error('본인 루틴만 변경할 수 있습니다.');
        }
    }

    private assertCanManageTodoGoal(user: User, goal: TodoGoal, category: TodoCategory | null = null): void {
        if (this.isAdminDataUser(user)) return;
        const ownsGoal = goal.ownerId === user.id || goal.userId === user.id;
        const ownsCategory = !category || category.ownerId === user.id || category.userId === user.id;
        if (!ownsGoal || !ownsCategory) {
            throw new Error('본인 카테고리만 변경할 수 있습니다.');
        }
    }

    private assertCanManageTodoCategory(user: User, category: TodoCategory): void {
        if (this.isAdminDataUser(user)) return;
        if (category.ownerId !== user.id && category.userId !== user.id) {
            throw new Error('본인 카테고리만 변경할 수 있습니다.');
        }
    }

    private assertCanManageTodoDailyRecord(user: User, record: TodoDailyRecord): void {
        if (this.isAdminDataUser(user)) return;
        if (record.userId !== user.id && record.ownerId !== user.id) {
            throw new Error('본인 하루 기록만 변경할 수 있습니다.');
        }
    }

    private assertCanManageDecoration(user: User, decoration: Decoration): void {
        if (this.isAdminDataUser(user)) return;
        if (decoration.userId !== user.id && decoration.ownerId !== user.id) {
            throw new Error('본인 꾸미기 설정만 변경할 수 있습니다.');
        }
    }

    private assertCanManageStickerAsset(user: User, asset: StickerAsset): void {
        if (this.isAdminDataUser(user)) return;
        if (asset.ownerId !== user.id) {
            throw new Error('본인 스티커만 변경할 수 있습니다.');
        }
    }

    private isOwnTodoRecord(userId: string, todo: Pick<Todo, 'authorId' | 'assigneeId' | 'createdBy' | 'assignedTo'>): boolean {
        return todo.authorId === userId
            || todo.assigneeId === userId
            || todo.createdBy === userId
            || todo.assignedTo === userId;
    }

    private isOwnRoutineRecord(userId: string, routine: Pick<TodoRoutine, 'userId' | 'ownerId' | 'assigneeId' | 'assignedTo'>): boolean {
        return routine.userId === userId
            || routine.ownerId === userId
            || routine.assigneeId === userId
            || routine.assignedTo === userId;
    }

    private normalizeTodoCategoryDeleteResolution(mode: TodoCategoryDeleteResolution): TodoCategoryDeleteResolution {
        if (mode === 'move' || mode === 'delete-todos') return mode;
        return 'uncategorized';
    }

    private todoMatchesGoalCategory(todo: Todo, goal: TodoGoal, category: TodoCategory | null): boolean {
        return todo.goalId === goal.id
            || Boolean(goal.categoryId && todo.categoryId === goal.categoryId)
            || Boolean(category?.id && todo.categoryId === category.id)
            || Boolean(!todo.goalId && todo.category && (todo.category === goal.name || todo.category === category?.name));
    }

    private routineMatchesGoalCategory(routine: TodoRoutine, goal: TodoGoal, category: TodoCategory | null): boolean {
        return routine.goalId === goal.id
            || Boolean(goal.categoryId && routine.categoryId === goal.categoryId)
            || Boolean(category?.id && routine.categoryId === category.id)
            || Boolean(!routine.goalId && routine.memo && (routine.memo === goal.name || routine.memo === category?.name));
    }

    private todoCategoryUsedByActiveGoal(state: OwnitPersistedState, categoryId: string, excludingGoalId: string): boolean {
        return state.todoGoals.some((goal) => (
            goal.id !== excludingGoalId
            && goal.categoryId === categoryId
            && !this.isDeletedRecord(goal)
        ));
    }

    private normalizeTodoDefaults(todo: Partial<Todo>, fallbackOrder = 0): Todo {
        const now = new Date().toISOString();
        const createdAt = todo.createdAt || todo.updatedAt || now;
        const updatedAt = todo.updatedAt || createdAt;
        const authorId = todo.authorId || todo.createdBy || DEFAULT_GUEST_USER_ID;
        const assigneeId = todo.assigneeId ?? todo.assignedTo ?? authorId;
        const requestedScope = todo.scope || (todo.visibility === 'group' ? 'shared' : 'personal');
        const scope = requestedScope === 'shared' && todo.groupId ? 'shared' : 'personal';
        const visibility = todo.visibility || (scope === 'shared' ? 'group' : 'private');
        const movedFromDate = todo.movedFromDate || todo.postponedFromDate || null;
        const movedToDate = todo.movedToDate || todo.postponedToDate || null;
        const order = Number.isFinite(Number(todo.order)) ? Number(todo.order) : fallbackOrder;
        const reminders = this.normalizeReminderSettings(todo.reminders);
        const legacyReminder = reminders[0] || null;
        const fallbackReminderAt = todo.reminderAt
            || todo.reminderRule?.nextAt
            || (legacyReminder ? this.reminderAtFromDateTime(todo.date || now.slice(0, 10), todo.reminderRule?.time || '09:00', legacyReminder.offsetMinutes) : null);
        const reminderRule = this.normalizeReminderRuleDefaults(todo.reminderRule, fallbackReminderAt, todo.date || now.slice(0, 10), authorId, 'custom', legacyReminder?.offsetMinutes || 0);

        return {
            ...todo,
            id: todo.id || `todo-${authorId}-${Date.now().toString(36)}`,
            title: todo.title || '',
            date: todo.date || now.slice(0, 10),
            completed: Boolean(todo.completed),
            completedAt: todo.completed ? (todo.completedAt || updatedAt) : null,
            goalId: todo.goalId || null,
            categoryId: todo.categoryId || null,
            routineId: todo.routineId || null,
            color: neutralizeLegacyOwnitColor(todo.color || '#6B6259'),
            order,
            category: todo.category || '미분류',
            authorId,
            assigneeId,
            createdBy: todo.createdBy || authorId,
            assignedTo: todo.assignedTo ?? assigneeId,
            scope,
            visibility: scope === 'shared' ? (visibility === 'public' ? 'public' : 'group') : 'private',
            repeatRule: todo.repeatRule || null,
            reminders,
            reminderAt: todo.reminderAt || reminderRule?.nextAt || null,
            reminderRule,
            memo: todo.memo || '',
            movedFromDate,
            movedToDate,
            timerSessionIds: Array.isArray(todo.timerSessionIds) ? todo.timerSessionIds : [],
            photoProofUrl: todo.photoProofUrl || '',
            postponedFromDate: movedFromDate,
            postponedToDate: movedToDate,
            timerSeconds: Number(todo.timerSeconds) || 0,
            diaryId: todo.diaryId || null,
            reactionId: todo.reactionId || null,
            completionRecordId: todo.completionRecordId || null,
            createdAt,
            updatedAt
        };
    }

    private normalizeTodoCategoryDefaults(category: Partial<TodoCategory>, fallbackOrder = 0): TodoCategory {
        const now = new Date().toISOString();
        const legacy = category as Partial<TodoCategory> & { category?: string; target?: string; sortOrder?: number; icon?: string; marker?: string; archived?: boolean; archivedAt?: string | null };
        const userId = category.userId || category.ownerId || DEFAULT_GUEST_USER_ID;
        const createdAt = category.createdAt || category.updatedAt || now;
        const order = Number.isFinite(Number(category.order))
            ? Number(category.order)
            : (Number.isFinite(Number(legacy.sortOrder)) ? Number(legacy.sortOrder) : fallbackOrder);
        const archivedAt = legacy.archivedAt || null;
        return {
            ...category,
            id: category.id || `todo-category-${userId}-${fallbackOrder}`,
            userId,
            name: category.name || legacy.category || legacy.target || '카테고리',
            color: neutralizeLegacyOwnitColor(category.color || '#6B6259'),
            icon: legacy.icon || '',
            marker: legacy.marker || legacy.icon || 'dot',
            visibility: category.visibility || 'private',
            order,
            sortOrder: order,
            collapsed: Boolean(category.collapsed),
            locked: Boolean(category.locked),
            archived: Boolean(legacy.archived || archivedAt),
            archivedAt,
            ownerId: category.ownerId || userId,
            createdAt,
            updatedAt: category.updatedAt || createdAt
        };
    }

    private normalizeTodoGoalDefaults(goal: Partial<TodoGoal>, fallbackOrder = 0): TodoGoal {
        const now = new Date().toISOString();
        const legacy = goal as Partial<TodoGoal> & { category?: string; target?: string; sortOrder?: number; archived?: boolean; archivedAt?: string | null };
        const userId = goal.userId || goal.ownerId || DEFAULT_GUEST_USER_ID;
        const createdAt = goal.createdAt || goal.updatedAt || now;
        const order = Number.isFinite(Number(goal.order))
            ? Number(goal.order)
            : (Number.isFinite(Number(legacy.sortOrder)) ? Number(legacy.sortOrder) : fallbackOrder);
        return {
            ...goal,
            id: goal.id || `todo-goal-${userId}-${fallbackOrder}`,
            userId,
            name: goal.name || legacy.category || legacy.target || '카테고리',
            color: neutralizeLegacyOwnitColor(goal.color || '#6B6259'),
            visibility: goal.visibility || 'private',
            order,
            collapsed: Boolean(goal.collapsed),
            locked: Boolean(goal.locked),
            ownerId: goal.ownerId || userId,
            categoryId: goal.categoryId || null,
            status: goal.status || (legacy.archived || legacy.archivedAt ? 'archived' : 'active'),
            createdAt,
            updatedAt: goal.updatedAt || createdAt
        };
    }

    private normalizeTodoRoutineDefaults(routine: Partial<TodoRoutine>): TodoRoutine {
        const now = new Date().toISOString();
        const userId = routine.userId || routine.ownerId || DEFAULT_GUEST_USER_ID;
        const createdAt = routine.createdAt || routine.updatedAt || now;
        const repeatRule = routine.repeatRule || {
            frequency: routine.repeatType && routine.repeatType !== 'custom' ? routine.repeatType : 'weekly',
            interval: 1,
            byWeekday: Array.isArray(routine.repeatDays) ? routine.repeatDays : [],
            until: routine.endDate || null,
            count: null
        };
        const reminders = this.normalizeReminderSettings(Array.isArray(routine.reminders) ? routine.reminders : (routine.reminder ? [routine.reminder] : []));
        const requestedScope = routine.scope || (routine.visibility === 'group' ? 'shared' : 'personal');
        const scope = requestedScope === 'shared' && routine.groupId ? 'shared' : 'personal';
        const assigneeId = routine.assigneeId ?? routine.assignedTo ?? userId;
        const legacyRoutineReminder = reminders[0] || null;
        const reminderAt = routine.reminderAt
            || routine.reminderRule?.nextAt
            || (legacyRoutineReminder ? this.reminderAtFromDateTime(routine.startDate || now.slice(0, 10), routine.reminderRule?.time || '09:00', legacyRoutineReminder.offsetMinutes) : null);
        const reminderRule = this.normalizeReminderRuleDefaults(routine.reminderRule, reminderAt, null, userId, 'routine', reminders[0]?.offsetMinutes || 0);

        return {
            ...routine,
            id: routine.id || `todo-routine-${userId}-${Date.now().toString(36)}`,
            userId,
            title: routine.title || '',
            goalId: routine.goalId || null,
            categoryId: routine.categoryId || null,
            color: neutralizeLegacyOwnitColor(routine.color || '#B59A5C'),
            repeatType: routine.repeatType || repeatRule.frequency,
            repeatDays: Array.isArray(routine.repeatDays) ? routine.repeatDays : (repeatRule.byWeekday || []),
            repeatDates: Array.isArray(routine.repeatDates) ? routine.repeatDates : [],
            startDate: routine.startDate || now.slice(0, 10),
            endDate: routine.endDate || repeatRule.until || null,
            reminder: routine.reminder || reminders[0] || null,
            reminderAt,
            reminderRule,
            hideToday: Boolean(routine.hideToday),
            visibility: scope === 'shared' ? (routine.visibility === 'public' ? 'public' : 'group') : 'private',
            ownerId: routine.ownerId || userId,
            assigneeId,
            assignedTo: routine.assignedTo ?? assigneeId,
            scope,
            repeatRule,
            reminders,
            memo: routine.memo || '',
            createdAt,
            updatedAt: routine.updatedAt || createdAt
        };
    }

    private normalizeReminderSettings(reminders: Partial<ReminderSetting>[] | null | undefined): ReminderSetting[] {
        if (!Array.isArray(reminders)) return [];
        return reminders
            .filter((reminder) => reminder && reminder.enabled !== false)
            .map((reminder, index) => ({
                id: reminder.id || `reminder-${Date.now().toString(36)}-${index}`,
                offsetMinutes: this.normalizeReminderOffset(reminder.offsetMinutes),
                channel: reminder.channel || 'app',
                enabled: reminder.enabled !== false
            }));
    }

    private normalizeReminderRuleDefaults(rule: Partial<ReminderRule> | null | undefined, fallbackAt: string | null, fallbackDate: string | null, fallbackUserId: string, fallbackKind: ReminderRule['kind'], fallbackOffsetMinutes = 0): ReminderRule | null {
        if (!rule && !fallbackAt) return null;

        const nextAt = rule?.nextAt || fallbackAt || null;
        const time = this.normalizeReminderTime(rule?.time || this.localTimeFromIso(nextAt) || '09:00');
        const date = rule?.date ?? (fallbackKind === 'routine' ? null : (fallbackDate || this.localDateFromIso(nextAt)));
        const offsetMinutes = this.normalizeReminderOffset(rule?.offsetMinutes ?? fallbackOffsetMinutes);

        return {
            kind: rule?.kind || fallbackKind,
            date: date || null,
            time,
            nextAt,
            timezone: rule?.timezone || this.localTimezone(),
            userId: rule?.userId || fallbackUserId || DEFAULT_GUEST_USER_ID,
            enabled: rule?.enabled !== false,
            reminderPreset: rule?.reminderPreset || this.reminderPresetFromOffset(offsetMinutes),
            offsetMinutes
        };
    }

    private normalizeReminderOffset(value: unknown): number {
        const offset = Number(value);
        if (!Number.isFinite(offset) || offset < 0) return 0;
        return Math.round(offset);
    }

    private reminderPresetFromOffset(offsetMinutes: number): ReminderRule['reminderPreset'] {
        if (offsetMinutes === 0) return 'at_time';
        if (offsetMinutes === 5) return 'before_5';
        if (offsetMinutes === 10) return 'before_10';
        if (offsetMinutes === 30) return 'before_30';
        if (offsetMinutes === 60) return 'before_60';
        if (offsetMinutes === 1440) return 'before_1440';
        return 'custom';
    }

    private reminderAtFromDateTime(dateKey: string, time: string, offsetMinutes: number): string | null {
        if (!dateKey) return null;
        const [year, month, day] = dateKey.split('-').map((value) => Number(value));
        const [hour, minute] = this.normalizeReminderTime(time).split(':').map((value) => Number(value));
        if (!year || !month || !day) return null;
        return new Date(year, month - 1, day, hour, minute - this.normalizeReminderOffset(offsetMinutes), 0, 0).toISOString();
    }

    private normalizeReminderTime(value: string): string {
        const match = /^(\d{1,2}):(\d{2})/.exec(value || '');
        if (!match) return '09:00';
        const hour = Math.min(23, Math.max(0, Number(match[1]) || 0));
        const minute = Math.min(59, Math.max(0, Number(match[2]) || 0));
        return `${`${hour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')}`;
    }

    private localDateFromIso(value: string | null): string | null {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value.slice(0, 10) || null;
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private localTimeFromIso(value: string | null): string | null {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value.slice(11, 16) || null;
        return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
    }

    private localTimezone(): string {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
        } catch (error) {
            return 'local';
        }
    }

    private normalizeTodoDailyRecordDefaults(record: Partial<TodoDailyRecord>): TodoDailyRecord {
        const now = new Date().toISOString();
        const userId = record.userId || DEFAULT_GUEST_USER_ID;
        const date = record.date || now.slice(0, 10);
        const createdAt = record.createdAt || record.updatedAt || now;
        return {
            ...record,
            id: record.id || `todo-daily-${userId}-${date}`,
            userId,
            date,
            completedCount: Number(record.completedCount) || 0,
            totalCount: Number(record.totalCount) || 0,
            categoryStats: Array.isArray(record.categoryStats) ? record.categoryStats.map((stat) => ({
                categoryId: stat.categoryId || null,
                goalId: stat.goalId || null,
                name: stat.name || '기타',
                color: neutralizeLegacyOwnitColor(stat.color || '#6B6259'),
                completedCount: Number(stat.completedCount) || 0,
                totalCount: Number(stat.totalCount) || 0,
                timerSeconds: Number(stat.timerSeconds) || 0
            })) : [],
            representativeEmoji: record.representativeEmoji || '',
            diary: record.diary || '',
            totalTimerSeconds: Number(record.totalTimerSeconds) || 0,
            receiptId: record.receiptId || null,
            receiptSnapshot: record.receiptSnapshot ? {
                date: record.receiptSnapshot.date || date,
                eventIds: Array.isArray(record.receiptSnapshot.eventIds) ? record.receiptSnapshot.eventIds : [],
                personalScheduleEventIds: Array.isArray(record.receiptSnapshot.personalScheduleEventIds) ? record.receiptSnapshot.personalScheduleEventIds : [],
                sharedScheduleEventIds: Array.isArray(record.receiptSnapshot.sharedScheduleEventIds) ? record.receiptSnapshot.sharedScheduleEventIds : [],
                completedTodoIds: Array.isArray(record.receiptSnapshot.completedTodoIds) ? record.receiptSnapshot.completedTodoIds : [],
                openTodoIds: Array.isArray(record.receiptSnapshot.openTodoIds) ? record.receiptSnapshot.openTodoIds : [],
                completedTodoTitles: Array.isArray(record.receiptSnapshot.completedTodoTitles) ? record.receiptSnapshot.completedTodoTitles : [],
                unfinishedTodoCount: Number(record.receiptSnapshot.unfinishedTodoCount) || 0,
                routineRecordIds: Array.isArray(record.receiptSnapshot.routineRecordIds) ? record.receiptSnapshot.routineRecordIds : [],
                routineTitles: Array.isArray(record.receiptSnapshot.routineTitles) ? record.receiptSnapshot.routineTitles : [],
                includeSharedScheduleSummary: record.receiptSnapshot.includeSharedScheduleSummary !== false,
                dailyMemo: record.receiptSnapshot.dailyMemo || record.diary || '',
                completedCount: Number(record.receiptSnapshot.completedCount) || 0,
                totalCount: Number(record.receiptSnapshot.totalCount) || 0,
                completionRate: Number(record.receiptSnapshot.completionRate) || 0,
                totalTimerSeconds: Number(record.receiptSnapshot.totalTimerSeconds) || 0,
                representativeEmoji: record.receiptSnapshot.representativeEmoji || record.representativeEmoji || '',
                moodEmoji: record.receiptSnapshot.moodEmoji || record.receiptSnapshot.representativeEmoji || record.representativeEmoji || '',
                moodLabel: record.receiptSnapshot.moodLabel || '',
                updatedAt: record.receiptSnapshot.updatedAt || record.updatedAt || createdAt
            } : null,
            createdAt,
            updatedAt: record.updatedAt || createdAt
        };
    }

    private normalizeTodoCompletionRecordDefaults(record: Partial<TodoCompletionRecord>): TodoCompletionRecord {
        const now = new Date().toISOString();
        const date = record.date || now.slice(0, 10);
        const authorId = record.authorId || DEFAULT_GUEST_USER_ID;
        const createdAt = record.createdAt || record.updatedAt || now;
        const completed = Boolean(record.completed);
        const targetId = record.routineId || record.todoId || 'item';
        return {
            ...record,
            id: record.id || `todo-completion-${authorId}-${targetId}-${date}`,
            todoId: record.todoId || null,
            routineId: record.routineId || null,
            date,
            completed,
            completedAt: completed ? (record.completedAt || now) : null,
            authorId,
            reactionId: record.reactionId || null,
            diaryId: record.diaryId || null,
            createdAt,
            updatedAt: record.updatedAt || createdAt
        };
    }

    private normalizeTodoPostponeRecordDefaults(record: Partial<TodoPostponeRecord>): TodoPostponeRecord {
        const now = new Date().toISOString();
        const fromDate = record.fromDate || now.slice(0, 10);
        const toDate = record.toDate || fromDate;
        const targetId = record.routineId || record.todoId || 'item';
        return {
            ...record,
            id: record.id || `todo-postpone-${targetId}-${fromDate}-${toDate}`,
            todoId: record.todoId || null,
            routineId: record.routineId || null,
            fromDate,
            toDate,
            reason: record.reason || '',
            createdAt: record.createdAt || now
        };
    }

    private normalizeTodoTimerSessionDefaults(session: Partial<TodoTimerSession>): TodoTimerSession {
        const now = new Date().toISOString();
        const startedAt = session.startedAt || session.createdAt || now;
        const endedAt = session.endedAt || null;
        const startedDate = session.startedDate || session.date || startedAt.slice(0, 10);
        const endedDate = session.endedDate || (endedAt ? endedAt.slice(0, 10) : null);
        const createdAt = session.createdAt || startedAt;
        return {
            ...session,
            id: session.id || `todo-timer-${session.todoId || 'todo'}-${Date.now().toString(36)}`,
            todoId: session.todoId || '',
            date: session.date || startedDate,
            status: session.status || 'completed',
            startedAt,
            startedDate,
            endedAt,
            endedDate,
            durationSeconds: Math.max(0, Math.round(Number(session.durationSeconds) || 0)),
            createdAt,
            updatedAt: session.updatedAt || endedAt || createdAt
        };
    }

    private normalizeTodoReactionDefaults(reaction: Partial<TodoReaction>): TodoReaction {
        const now = new Date().toISOString();
        const userId = reaction.userId || reaction.authorId || DEFAULT_GUEST_USER_ID;
        const createdAt = reaction.createdAt || reaction.updatedAt || now;
        return {
            ...reaction,
            id: reaction.id || `todo-reaction-${userId}-${Date.now().toString(36)}`,
            userId,
            date: reaction.date || now.slice(0, 10),
            authorId: reaction.authorId || userId,
            targetType: reaction.targetType || 'day',
            targetId: reaction.targetId || null,
            kind: reaction.kind || 'none',
            emoji: reaction.emoji || '',
            message: reaction.message || '',
            createdAt,
            updatedAt: reaction.updatedAt || createdAt
        };
    }

    private withTodoTimerSessionIds(todos: Todo[], sessions: TodoTimerSession[]): Todo[] {
        const sessionIdsByTodo = new Map<string, string[]>();
        for (const session of sessions) {
            const ids = sessionIdsByTodo.get(session.todoId) || [];
            ids.push(session.id);
            sessionIdsByTodo.set(session.todoId, ids);
        }

        return todos.map((todo) => {
            const sessionIds = new Set([...(todo.timerSessionIds || []), ...(sessionIdsByTodo.get(todo.id) || [])]);
            return { ...todo, timerSessionIds: Array.from(sessionIds) };
        });
    }

    private withDerivedDailyRecords(state: OwnitPersistedState, existingRecords: TodoDailyRecord[]): TodoDailyRecord[] {
        const existingByKey = new Map<string, TodoDailyRecord>();
        for (const record of existingRecords) {
            const normalized = this.normalizeTodoDailyRecordDefaults(record);
            existingByKey.set(this.dailyRecordKey(normalized.userId, normalized.date), normalized);
        }

        const sessionsByTodo = new Map<string, TodoTimerSession[]>();
        for (const session of state.todoTimerSessions) {
            if (this.isDeletedRecord(session)) continue;
            if (session.status !== 'completed') continue;
            const sessions = sessionsByTodo.get(session.todoId) || [];
            sessions.push(session);
            sessionsByTodo.set(session.todoId, sessions);
        }

        const derived = new Map<string, TodoDailyRecord>();
        const appendDailyStat = (
            userId: string,
            date: string,
            categoryId: string | null,
            goalId: string | null,
            name: string,
            color: string,
            completed: boolean,
            timerSeconds = 0
        ) => {
            const key = this.dailyRecordKey(userId, date);
            const existing = derived.get(key) || this.normalizeTodoDailyRecordDefaults({
                id: `todo-daily-${userId}-${date}`,
                userId,
                date
            });
            const categoryKey = `${categoryId || 'none'}::${goalId || 'none'}::${name || '기타'}`;
            const statsByKey = new Map(existing.categoryStats.map((stat) => [`${stat.categoryId || 'none'}::${stat.goalId || 'none'}::${stat.name}`, stat]));
            const stat = statsByKey.get(categoryKey) || {
                categoryId: categoryId || null,
                goalId: goalId || null,
                name: name || '기타',
                color: color || '#6B6259',
                completedCount: 0,
                totalCount: 0,
                timerSeconds: 0
            } as TodoDailyCategoryStat;

            stat.totalCount += 1;
            if (completed) stat.completedCount += 1;
            stat.timerSeconds += timerSeconds;
            statsByKey.set(categoryKey, stat);

            existing.totalCount += 1;
            if (completed) existing.completedCount += 1;
            existing.totalTimerSeconds += timerSeconds;
            existing.categoryStats = Array.from(statsByKey.values());
            derived.set(key, existing);
        };

        for (const todo of state.todos.filter((item) => !item.routineId && !this.isDeletedRecord(item))) {
            const userId = todo.createdBy || todo.authorId || DEFAULT_GUEST_USER_ID;
            const sessionSeconds = (sessionsByTodo.get(todo.id) || []).reduce((sum, session) => sum + (Number(session.durationSeconds) || 0), 0);
            const timerSeconds = sessionSeconds > 0 ? sessionSeconds : (Number(todo.timerSeconds) || 0);
            appendDailyStat(
                userId,
                todo.date,
                todo.categoryId || null,
                todo.goalId || null,
                todo.category || '기타',
                todo.color || '#6B6259',
                Boolean(todo.completed),
                timerSeconds
            );
        }

        for (const record of state.todoCompletionRecords.filter((item) => item.routineId && !this.isDeletedRecord(item))) {
            const routine = state.todoRoutines.find((item) => item.id === record.routineId && !this.isDeletedRecord(item));
            if (!routine) continue;
            const goal = state.todoGoals.find((item) => item.id === routine.goalId && !this.isDeletedRecord(item)) || null;
            const category = state.todoCategories.find((item) => item.id === routine.categoryId && !this.isDeletedRecord(item)) || null;
            appendDailyStat(
                record.authorId || routine.userId || DEFAULT_GUEST_USER_ID,
                record.date,
                routine.categoryId || null,
                routine.goalId || null,
                goal?.name || category?.name || '루틴',
                goal?.color || category?.color || routine.color || '#B59A5C',
                Boolean(record.completed),
                0
            );
        }

        const merged: TodoDailyRecord[] = [];
        for (const [key, record] of derived) {
            const existing = existingByKey.get(key);
            merged.push(this.normalizeTodoDailyRecordDefaults({
                ...record,
                representativeEmoji: existing?.representativeEmoji || record.representativeEmoji,
                diary: existing?.diary || record.diary,
                receiptId: existing?.receiptId || record.receiptId,
                receiptSnapshot: existing?.receiptSnapshot || record.receiptSnapshot,
                createdAt: existing?.createdAt || record.createdAt,
                updatedAt: existing?.updatedAt || record.updatedAt
            }));
        }

        for (const [key, record] of existingByKey) {
            if (derived.has(key)) continue;
            const resetRecord = this.normalizeTodoDailyRecordDefaults({
                ...record,
                completedCount: 0,
                totalCount: 0,
                categoryStats: [],
                totalTimerSeconds: 0
            });
            if (this.hasTodoDailyRecordContent(resetRecord)) merged.push(resetRecord);
        }

        return merged.sort((a, b) => {
            if (a.userId !== b.userId) return a.userId.localeCompare(b.userId);
            return a.date.localeCompare(b.date);
        });
    }

    private dailyRecordKey(userId: string, date: string): string {
        return `${userId}::${date}`;
    }

    private hasTodoDailyRecordContent(record: TodoDailyRecord): boolean {
        return Boolean(
            (record.representativeEmoji || '').trim()
            || (record.diary || '').trim()
            || record.receiptId
            || record.receiptSnapshot
        );
    }

    private totalCompletedTimerSecondsForTodo(state: OwnitPersistedState, todoId: string): number {
        return state.todoTimerSessions
            .filter((session) => session.todoId === todoId && session.status === 'completed' && !this.isDeletedRecord(session))
            .reduce((sum, session) => sum + (Number(session.durationSeconds) || 0), 0);
    }

    private nextTodoOrder(todos: Todo[], todo: Partial<Todo>): number {
        if (Number.isFinite(Number(todo.order))) return Number(todo.order);
        const userId = todo.createdBy || todo.authorId || DEFAULT_GUEST_USER_ID;
        const date = todo.date || new Date().toISOString().slice(0, 10);
        const sameDayOrders = todos
            .filter((item) => item.date === date && !this.isDeletedRecord(item) && (item.createdBy === userId || item.authorId === userId))
            .map((item) => Number(item.order))
            .filter((order) => Number.isFinite(order));
        if (sameDayOrders.length === 0) return 0;
        return Math.max(...sameDayOrders) + 1;
    }

    private isAdminDataUser(user: Pick<User, 'id' | 'username' | 'role'> & Partial<Pick<User, 'email'>>): boolean {
        return user.role === 'admin';
    }

    private readJson<T>(key: string, fallback: T): T {
        const raw = this.storage.getItem(key);
        if (!raw) return fallback;
        try {
            return JSON.parse(raw) as T;
        } catch (error) {
            return fallback;
        }
    }

    private writeJson<T>(key: string, value: T): void {
        this.storage.setItem(key, JSON.stringify(value));
    }

    protected get storage(): Storage {
        if (typeof window === 'undefined' || !window.localStorage) {
            throw new Error('브라우저 저장소를 사용할 수 없습니다.');
        }
        return window.localStorage;
    }
}

interface SupabaseRecordRow {
    id: string;
    owner_id: string | null;
    group_id: string | null;
    user_id: string | null;
    author_id: string | null;
    assignee_id: string | null;
    admin_id: string | null;
    calendar_id: string | null;
    record_date: string | null;
    updated_at: string;
    deleted_at: string | null;
    payload: any;
}

type SupabaseTableKey = CollectionName | 'memberships';

const SUPABASE_TABLES: Record<SupabaseTableKey, string> = {
    users: 'user_profiles',
    calendars: 'calendars',
    groups: 'shared_groups',
    events: 'events',
    todos: 'todos',
    todoCategories: 'todo_categories',
    todoGoals: 'todo_goals',
    todoRoutines: 'routines',
    todoDailyRecords: 'daily_records',
    todoCompletionRecords: 'todo_completion_records',
    todoPostponeRecords: 'todo_postpone_records',
    todoTimerSessions: 'todo_timer_sessions',
    todoDiaries: 'todo_diaries',
    todoReactions: 'todo_reactions',
    stickerAssets: 'sticker_assets',
    decorations: 'decorations',
    themes: 'themes',
    memberships: 'memberships'
};

const CLOUD_COLLECTIONS: CollectionName[] = [
    'users',
    'calendars',
    'groups',
    'events',
    'todos',
    'todoCategories',
    'todoGoals',
    'todoRoutines',
    'todoDailyRecords',
    'todoCompletionRecords',
    'todoPostponeRecords',
    'todoTimerSessions',
    'todoDiaries',
    'todoReactions',
    'stickerAssets',
    'decorations',
    'themes'
];

interface CloudPushResult {
    pushedRecords: number;
    skippedStaleWrites: number;
}

interface CloudSyncMeta {
    lastSyncedAt: string;
    lastCheckedAt: string;
    pushedRecords: number;
    skippedStaleWrites: number;
}

export class OwnitSupabaseRepository extends OwnitLocalStorageRepository {
    private readonly requestTimeoutMs = 15000;
    private readonly cloudSyncMetaPrefix = 'ownit.cloud.sync.meta.v1';
    private activeCloudUser: User | null = null;
    private pendingPush = false;
    private pushTimer: number | null = null;
    private syncState: CloudRepositoryStatus['state'] = 'idle';
    private lastSyncedAt = '';
    private lastCheckedAt = '';
    private lastError = '';
    private pushedRecords = 0;
    private skippedStaleWrites = 0;
    private suppressCloudPush = false;

    constructor(private readonly config: OwnitSupabaseConfig) {
        super();
    }

    public override getAdapterInfo(): RepositoryAdapterInfo {
        return {
            kind: 'supabase',
            name: 'Ownit Supabase repository',
            currentVersion: OWNIT_DATA_VERSION,
            supportsRealtime: false,
            supportsOffline: true
        };
    }

    public override getSyncStatus(): RepositorySyncStatus {
        const local = super.getSyncStatus();
        return {
            ...local,
            ...this.getAdapterInfo(),
            pendingLocalChanges: this.pendingPush
        };
    }

    public override getCloudStatus(): CloudRepositoryStatus {
        const meta = this.readCloudSyncMeta();
        return {
            enabled: true,
            provider: 'supabase',
            label: 'Supabase',
            state: this.syncState,
            message: this.cloudStatusMessage(meta),
            lastSyncedAt: this.lastSyncedAt || meta.lastSyncedAt,
            lastCheckedAt: this.lastCheckedAt || meta.lastCheckedAt,
            lastError: this.lastError,
            pendingWrites: this.pendingPush,
            pushedRecords: this.pushedRecords,
            skippedStaleWrites: this.skippedStaleWrites
        };
    }

    public override loadForUser(user: User): OwnitDataSnapshot {
        this.activeCloudUser = normalizeDataUser(user);
        this.restoreCloudSyncMeta();
        this.suppressCloudPush = true;
        try {
            return super.loadForUser(this.activeCloudUser);
        } finally {
            this.suppressCloudPush = false;
        }
    }

    public override importSnapshotFromCloud(user: User, snapshot: Partial<OwnitDataSnapshot>): OwnitDataSnapshot {
        this.suppressCloudPush = true;
        try {
            return super.importSnapshotFromCloud(user, snapshot);
        } finally {
            this.suppressCloudPush = false;
        }
    }

    public async refreshFromCloud(user: User): Promise<OwnitDataSnapshot> {
        const normalizedUser = normalizeDataUser(user);
        this.activeCloudUser = normalizedUser;
        this.syncState = 'syncing';
        this.lastError = '';
        try {
            const remoteSnapshot = await this.fetchSnapshot(normalizedUser);
            const merged = this.importSnapshotFromCloud(normalizedUser, remoteSnapshot);
            this.recordCloudSyncMeta({
                lastSyncedAt: new Date().toISOString(),
                pushedRecords: 0,
                skippedStaleWrites: 0
            });
            this.syncState = 'idle';
            return merged;
        } catch (error) {
            this.captureCloudError(error, '클라우드 데이터를 가져오지 못했습니다.');
            return super.getSnapshotForUser(normalizedUser.id);
        }
    }

    public async importLocalDataToCloud(user: User): Promise<CloudImportResult> {
        const normalizedUser = normalizeDataUser(user);
        this.activeCloudUser = normalizedUser;
        this.syncState = 'syncing';
        this.lastError = '';
        try {
            const snapshot = this.exportLocalSnapshotWithoutScheduling(normalizedUser);
            const pushResult = await this.pushSnapshot(snapshot);
            const remoteSnapshot = await this.fetchSnapshot(normalizedUser);
            const pulledRecords = this.countSnapshotRecords(remoteSnapshot);
            this.importSnapshotFromCloud(normalizedUser, remoteSnapshot);
            this.pendingPush = false;
            this.recordCloudSyncMeta({
                lastSyncedAt: new Date().toISOString(),
                pushedRecords: pushResult.pushedRecords,
                skippedStaleWrites: pushResult.skippedStaleWrites
            });
            this.syncState = 'idle';
            return {
                importedAt: this.lastSyncedAt,
                pushedRecords: pushResult.pushedRecords,
                pulledRecords,
                skippedStaleWrites: pushResult.skippedStaleWrites
            };
        } catch (error) {
            this.captureCloudError(error, '로컬 데이터를 클라우드로 가져오지 못했습니다.');
            throw error;
        }
    }

    public async flushPendingWrites(): Promise<void> {
        if (!this.pendingPush || !this.activeCloudUser?.id) return;
        this.pendingPush = false;
        this.syncState = 'syncing';
        this.lastError = '';
        try {
            const snapshot = this.exportLocalSnapshotWithoutScheduling(this.activeCloudUser);
            const pushResult = await this.pushSnapshot(snapshot);
            if (pushResult.skippedStaleWrites > 0) {
                const remoteSnapshot = await this.fetchSnapshot(this.activeCloudUser);
                this.importSnapshotFromCloud(this.activeCloudUser, remoteSnapshot);
            }
            this.pendingPush = false;
            this.recordCloudSyncMeta({
                lastSyncedAt: new Date().toISOString(),
                pushedRecords: pushResult.pushedRecords,
                skippedStaleWrites: pushResult.skippedStaleWrites
            });
            this.syncState = 'idle';
        } catch (error) {
            this.pendingPush = true;
            this.captureCloudError(error, '클라우드 저장에 실패했습니다.');
        }
    }

    protected override writeState(state: OwnitPersistedState): void {
        super.writeState(state);
        if (!this.suppressCloudPush) this.scheduleCloudPush();
    }

    private scheduleCloudPush(): void {
        if (!this.activeCloudUser?.id) return;
        this.pendingPush = true;
        if (this.pushTimer !== null) return;
        this.pushTimer = window.setTimeout(async () => {
            this.pushTimer = null;
            await this.flushPendingWrites();
        }, 800);
    }

    private exportLocalSnapshotWithoutScheduling(user: User): OwnitDataSnapshot {
        this.suppressCloudPush = true;
        try {
            return super.exportSnapshotForCloud(user);
        } finally {
            this.suppressCloudPush = false;
        }
    }

    private async pushSnapshot(snapshot: OwnitDataSnapshot): Promise<CloudPushResult> {
        const rowsByTable = this.rowsByTable(snapshot);
        let pushedRecords = 0;
        let skippedStaleWrites = 0;
        for (const [table, rows] of rowsByTable.entries()) {
            if (rows.length === 0) continue;
            const result = await this.upsertRows(table, rows);
            pushedRecords += result.pushedRecords;
            skippedStaleWrites += result.skippedStaleWrites;
        }
        return { pushedRecords, skippedStaleWrites };
    }

    private rowsByTable(snapshot: OwnitDataSnapshot): Map<string, SupabaseRecordRow[]> {
        const rowsByTable = new Map<string, SupabaseRecordRow[]>();
        for (const collection of CLOUD_COLLECTIONS) {
            const table = SUPABASE_TABLES[collection];
            const records = this.snapshotRecords(snapshot, collection);
            rowsByTable.set(table, records
                .filter((record) => this.canPushRecordToCloud(collection, record, snapshot.user, snapshot.groups))
                .map((record) => this.rowForRecord(collection, record, snapshot.user.id)));
        }

        const membershipRows = snapshot.groups.flatMap((group) => {
            return (group.members || [])
                .filter((member) => this.canPushMembershipToCloud(group, member, snapshot.user, snapshot.groups))
                .map((member) => this.rowForMembership(group, member, snapshot.user.id));
        });
        rowsByTable.set(SUPABASE_TABLES.memberships, membershipRows);
        return rowsByTable;
    }

    private snapshotRecords(snapshot: Partial<OwnitDataSnapshot>, collection: CollectionName): Array<{ [key: string]: any }> {
        if (collection === 'users') return snapshot.user ? [snapshot.user] : [];
        const records = snapshot[collection as keyof OwnitDataSnapshot];
        return Array.isArray(records) ? records as Array<{ [key: string]: any }> : [];
    }

    private canPushRecordToCloud(collection: CollectionName, record: { [key: string]: any }, user: User, groups: Group[]): boolean {
        if (!record?.id || !user?.id) return false;
        if (this.isAdminCloudUser(user)) return true;
        if (collection === 'themes') return this.ownsCloudRecord(record, user.id);

        const groupId = record.groupId || (collection === 'groups' ? record.id : null);
        if (groupId) return this.canPushGroupedRecordToCloud(collection, record, String(groupId), user, groups);
        return this.ownsCloudRecord(record, user.id);
    }

    private canPushGroupedRecordToCloud(collection: CollectionName, record: { [key: string]: any }, groupId: string, user: User, groups: Group[]): boolean {
        const role = this.cloudGroupRole(groupId, user.id, groups);
        if (role === 'owner' || role === 'admin') return true;
        if (role !== 'member') return false;

        if (collection === 'groups' || collection === 'calendars') return false;
        return this.ownsCloudRecord(record, user.id);
    }

    private canPushMembershipToCloud(group: Group, member: GroupMember, user: User, groups: Group[]): boolean {
        if (!member?.id || !group?.id || !user?.id) return false;
        if (this.isAdminCloudUser(user)) return true;
        const role = this.cloudGroupRole(group.id, user.id, groups);
        return role === 'owner' || role === 'admin';
    }

    private cloudGroupRole(groupId: string, userId: string, groups: Group[]): string {
        const group = groups.find((item) => item.id === groupId) || null;
        const member = group?.members?.find((item) => item.userId === userId && item.status === 'active') || null;
        return member?.role || '';
    }

    private ownsCloudRecord(record: { [key: string]: any }, userId: string): boolean {
        return record.ownerId === userId
            || record.userId === userId
            || record.authorId === userId
            || record.createdBy === userId
            || record.assigneeId === userId
            || record.assignedTo === userId
            || record.adminId === userId
            || record.id === userId;
    }

    private rowForRecord(collection: CollectionName, record: { [key: string]: any }, fallbackOwnerId: string): SupabaseRecordRow {
        const ownerId = record.ownerId || record.userId || record.authorId || record.createdBy || record.adminId || (collection === 'users' ? record.id : fallbackOwnerId);
        const userId = record.userId || (collection === 'users' ? record.id : null);
        const groupId = record.groupId || (collection === 'groups' ? record.id : null);
        return {
            id: record.id,
            owner_id: ownerId || fallbackOwnerId || null,
            group_id: groupId || null,
            user_id: userId || null,
            author_id: record.authorId || record.createdBy || null,
            assignee_id: record.assigneeId || record.assignedTo || null,
            admin_id: record.adminId || null,
            calendar_id: record.calendarId || null,
            record_date: record.date || record.startDate || null,
            updated_at: record.updatedAt || record.createdAt || new Date().toISOString(),
            deleted_at: record.deletedAt || null,
            payload: record
        };
    }

    private rowForMembership(group: Group, member: GroupMember, fallbackOwnerId: string): SupabaseRecordRow {
        const updatedAt = (member as Partial<GroupMember> & { updatedAt?: string }).updatedAt || group.updatedAt || group.createdAt || new Date().toISOString();
        const deletedAt = (member as Partial<GroupMember> & { deletedAt?: string | null }).deletedAt || group.deletedAt || null;
        return {
            id: member.id,
            owner_id: group.adminId || fallbackOwnerId || null,
            group_id: member.groupId || group.id,
            user_id: member.userId || null,
            author_id: null,
            assignee_id: null,
            admin_id: group.adminId || null,
            calendar_id: null,
            record_date: null,
            updated_at: updatedAt,
            deleted_at: deletedAt,
            payload: member
        };
    }

    private async fetchSnapshot(user: User): Promise<Partial<OwnitDataSnapshot>> {
        const normalizedUser = normalizeDataUser(user);
        const ownMembershipRows = await this.selectRows(SUPABASE_TABLES.memberships, normalizedUser, []);
        const ownMemberships = ownMembershipRows.map((row) => row.payload as GroupMember).filter((member) => member?.id);
        const groupIds = Array.from(new Set(ownMemberships.map((member) => member.groupId).filter(Boolean)));
        const membershipRows = groupIds.length > 0
            ? await this.selectRows(SUPABASE_TABLES.memberships, normalizedUser, groupIds)
            : ownMembershipRows;
        const memberships = membershipRows.map((row) => row.payload as GroupMember).filter((member) => member?.id);
        const partial: Partial<OwnitDataSnapshot> = { user: normalizedUser };

        for (const collection of CLOUD_COLLECTIONS) {
            const table = SUPABASE_TABLES[collection];
            const rows = await this.selectRows(table, normalizedUser, groupIds);
            (partial as any)[collection] = rows.map((row) => row.payload).filter((record) => record?.id);
        }

        const userRows = await this.selectRowsById(SUPABASE_TABLES.users, normalizedUser.id);
        if (userRows[0]?.payload) partial.user = normalizeDataUser(userRows[0].payload as User);

        const groupMembersByGroup = new Map<string, GroupMember[]>();
        for (const member of memberships) {
            const members = groupMembersByGroup.get(member.groupId) || [];
            members.push(member);
            groupMembersByGroup.set(member.groupId, members);
        }
        partial.groups = (partial.groups || []).map((group) => {
            const members = groupMembersByGroup.get(group.id);
            if (!members?.length) return group;
            return { ...group, members };
        });

        return partial;
    }

    private async selectRowsById(table: string, id: string): Promise<SupabaseRecordRow[]> {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('id', `eq.${id}`);
        return this.restRequest<SupabaseRecordRow[]>(table, 'GET', params);
    }

    private async selectRows(table: string, user: User, groupIds: string[]): Promise<SupabaseRecordRow[]> {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('order', 'updated_at.asc');
        if (!this.isAdminCloudUser(user)) {
            const filters = [
                `owner_id.eq.${this.escapeFilterValue(user.id)}`,
                `user_id.eq.${this.escapeFilterValue(user.id)}`,
                `author_id.eq.${this.escapeFilterValue(user.id)}`,
                `assignee_id.eq.${this.escapeFilterValue(user.id)}`,
                `admin_id.eq.${this.escapeFilterValue(user.id)}`
            ];
            if (groupIds.length > 0) {
                filters.push(`group_id.in.(${this.postgrestList(groupIds)})`);
            }
            params.set('or', `(${filters.join(',')})`);
        }
        return this.restRequest<SupabaseRecordRow[]>(table, 'GET', params);
    }

    private async upsertRows(table: string, rows: SupabaseRecordRow[]): Promise<CloudPushResult> {
        const uniqueRows = this.dedupeRowsByNewestRevision(rows);
        if (uniqueRows.length === 0) return { pushedRecords: 0, skippedStaleWrites: 0 };

        const existingRows = await this.selectRowsByIds(table, uniqueRows.map((row) => row.id));
        const existingById = new Map(existingRows.map((row) => [row.id, row]));
        const rowsToPush: SupabaseRecordRow[] = [];
        let skippedStaleWrites = 0;

        for (const row of uniqueRows) {
            const existing = existingById.get(row.id);
            if (!existing || this.shouldPushRow(row, existing)) {
                rowsToPush.push(row);
                continue;
            }
            skippedStaleWrites += 1;
        }

        if (rowsToPush.length === 0) {
            return { pushedRecords: 0, skippedStaleWrites };
        }

        const params = new URLSearchParams();
        params.set('on_conflict', 'id');
        await this.restRequest(table, 'POST', params, rowsToPush, {
            Prefer: 'resolution=merge-duplicates,return=minimal'
        });
        return {
            pushedRecords: rowsToPush.length,
            skippedStaleWrites
        };
    }

    private dedupeRowsByNewestRevision(rows: SupabaseRecordRow[]): SupabaseRecordRow[] {
        const byId = new Map<string, SupabaseRecordRow>();
        for (const row of rows) {
            if (!row.id) continue;
            const current = byId.get(row.id);
            if (!current || this.shouldPushRow(row, current)) byId.set(row.id, row);
        }
        return Array.from(byId.values());
    }

    private shouldPushRow(localRow: SupabaseRecordRow, remoteRow: SupabaseRecordRow): boolean {
        const localPayload = this.rowRevisionPayload(localRow);
        const remotePayload = this.rowRevisionPayload(remoteRow);
        return this.shouldReplaceRecord(remotePayload, localPayload);
    }

    private rowRevisionPayload(row: SupabaseRecordRow): { updatedAt?: string; syncVersion?: number } {
        const payload = row.payload && typeof row.payload === 'object' ? row.payload : {};
        return {
            updatedAt: payload.updatedAt || row.updated_at,
            syncVersion: Number(payload.syncVersion) || 0
        };
    }

    private async selectRowsByIds(table: string, ids: string[]): Promise<SupabaseRecordRow[]> {
        const result: SupabaseRecordRow[] = [];
        const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
        const chunkSize = 80;
        for (let index = 0; index < uniqueIds.length; index += chunkSize) {
            const chunk = uniqueIds.slice(index, index + chunkSize);
            const params = new URLSearchParams();
            params.set('select', 'id,updated_at,payload');
            params.set('id', `in.(${this.postgrestList(chunk)})`);
            const rows = await this.restRequest<SupabaseRecordRow[]>(table, 'GET', params);
            result.push(...rows);
        }
        return result;
    }

    private async restRequest<T = unknown>(table: string, method: string, params: URLSearchParams, body?: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
        const query = params.toString();
        const accessToken = getStoredSupabaseAccessToken();
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeout = controller
            ? window.setTimeout(() => controller.abort(), this.requestTimeoutMs)
            : null;
        let response: Response;
        try {
            response = await fetch(`${this.config.url}/rest/v1/${table}${query ? `?${query}` : ''}`, {
                method,
                headers: {
                    apikey: this.config.anonKey,
                    Authorization: `Bearer ${accessToken || this.config.anonKey}`,
                    'Content-Type': 'application/json',
                    'Accept-Profile': this.config.schema,
                    'Content-Profile': this.config.schema,
                    ...extraHeaders
                },
                body: body === undefined ? undefined : JSON.stringify(body),
                signal: controller?.signal
            });
        } catch (error) {
            if (this.isAbortError(error)) {
                throw new Error('클라우드 응답이 지연되어 요청을 중단했습니다. 네트워크 상태를 확인해 주세요.');
            }
            throw error;
        } finally {
            if (timeout !== null) window.clearTimeout(timeout);
        }

        if (!response.ok) {
            const message = await this.readSupabaseError(response, `${table} 요청이 실패했습니다.`);
            if (this.isPermissionResponse(response.status, message)) {
                throw new RepositoryPermissionError(this.permissionErrorMessage(table, method), response.status);
            }
            throw new Error(message);
        }

        if (response.status === 204) return undefined as T;
        return await response.json() as T;
    }

    private isAbortError(error: unknown): boolean {
        return (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError')
            || (error instanceof Error && error.name === 'AbortError');
    }

    private async readSupabaseError(response: Response, fallback: string): Promise<string> {
        try {
            const data = await response.json();
            return data.message || data.details || data.hint || data.error || fallback;
        } catch (error) {
            return fallback;
        }
    }

    private isPermissionResponse(status: number, message: string): boolean {
        const normalized = (message || '').toLowerCase();
        return status === 401
            || status === 403
            || normalized.includes('row-level security')
            || normalized.includes('permission denied')
            || normalized.includes('42501')
            || normalized.includes('not authorized')
            || normalized.includes('unauthorized');
    }

    private permissionErrorMessage(table: string, method: string): string {
        const action = method === 'GET' ? '조회' : '저장';
        return `권한이 없어 ${table} 데이터를 ${action}하지 못했습니다. 로그인 계정, 그룹 멤버십, 역할 권한을 확인해 주세요.`;
    }

    private restoreCloudSyncMeta(): void {
        const meta = this.readCloudSyncMeta();
        this.lastSyncedAt = meta.lastSyncedAt;
        this.lastCheckedAt = meta.lastCheckedAt;
        this.pushedRecords = meta.pushedRecords;
        this.skippedStaleWrites = meta.skippedStaleWrites;
    }

    private recordCloudSyncMeta(patch: Partial<CloudSyncMeta> & Pick<CloudSyncMeta, 'lastSyncedAt'>): void {
        const now = new Date().toISOString();
        const previous = this.readCloudSyncMeta();
        const next: CloudSyncMeta = {
            lastSyncedAt: patch.lastSyncedAt,
            lastCheckedAt: patch.lastCheckedAt || now,
            pushedRecords: Number(patch.pushedRecords ?? previous.pushedRecords) || 0,
            skippedStaleWrites: Number(patch.skippedStaleWrites ?? previous.skippedStaleWrites) || 0
        };
        this.lastSyncedAt = next.lastSyncedAt;
        this.lastCheckedAt = next.lastCheckedAt;
        this.pushedRecords = next.pushedRecords;
        this.skippedStaleWrites = next.skippedStaleWrites;
        try {
            globalThis.localStorage?.setItem(this.cloudSyncMetaKey(), JSON.stringify(next));
        } catch (error) {
            // Sync metadata is advisory and must not block data writes.
        }
    }

    private readCloudSyncMeta(): CloudSyncMeta {
        try {
            const raw = globalThis.localStorage?.getItem(this.cloudSyncMetaKey());
            if (!raw) return this.emptyCloudSyncMeta();
            const parsed = JSON.parse(raw) as Partial<CloudSyncMeta>;
            return {
                lastSyncedAt: typeof parsed.lastSyncedAt === 'string' ? parsed.lastSyncedAt : '',
                lastCheckedAt: typeof parsed.lastCheckedAt === 'string' ? parsed.lastCheckedAt : '',
                pushedRecords: Number(parsed.pushedRecords) || 0,
                skippedStaleWrites: Number(parsed.skippedStaleWrites) || 0
            };
        } catch (error) {
            return this.emptyCloudSyncMeta();
        }
    }

    private emptyCloudSyncMeta(): CloudSyncMeta {
        return {
            lastSyncedAt: '',
            lastCheckedAt: '',
            pushedRecords: 0,
            skippedStaleWrites: 0
        };
    }

    private cloudSyncMetaKey(): string {
        return `${this.cloudSyncMetaPrefix}.${this.activeCloudUser?.id || 'anonymous'}`;
    }

    private cloudStatusMessage(meta: CloudSyncMeta): string {
        if (this.lastError) return this.lastError;
        if (this.syncState === 'syncing') return 'Supabase와 동기화 중입니다.';
        if (this.pendingPush) return '로컬 변경사항을 클라우드에 저장할 준비 중입니다.';
        const skipped = this.skippedStaleWrites || meta.skippedStaleWrites;
        if (skipped > 0) return `클라우드 최신 데이터 ${skipped}건은 오래된 로컬 캐시로 덮어쓰지 않았습니다.`;
        if (this.lastSyncedAt || meta.lastSyncedAt) return 'Supabase 동기화가 준비되었습니다.';
        return 'Supabase 환경변수가 감지되었습니다. 로컬 데이터를 유지하면서 클라우드 연결을 준비합니다.';
    }

    private captureCloudError(error: unknown, fallback: string): void {
        this.syncState = 'error';
        this.lastError = error instanceof Error && error.message ? error.message : fallback;
    }

    private countSnapshotRecords(snapshot: Partial<OwnitDataSnapshot>): number {
        return CLOUD_COLLECTIONS.reduce((sum, collection) => {
            return sum + this.snapshotRecords(snapshot, collection).length;
        }, 0);
    }

    private isAdminCloudUser(user: Pick<User, 'id' | 'username' | 'role'> & Partial<Pick<User, 'email'>>): boolean {
        return user.role === 'admin' && Boolean(getStoredSupabaseAccessToken());
    }

    private postgrestList(values: string[]): string {
        return values.map((value) => `"${this.escapeFilterValue(value).replace(/"/g, '\\"')}"`).join(',');
    }

    private escapeFilterValue(value: string): string {
        return (value || '').replace(/[(),]/g, '');
    }
}
